// src/pages/ScanPage.tsx
import React, { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { DecodeHintType, BarcodeFormat, Result } from "@zxing/library";
import { apiUrl } from "../config/api";

// ---------- Types ----------
type Product = {
  code: string;
  name: string;
  price: number;
  unit?: string;
  estimated_unit_cost?: number | null;
  available_qty?: number;
};

type ItemEntry = {
  product: Product;
  qty: number;
};

type KC_Item = {
  keychainId: number;
  label: string;
  qty: number;
  unitPrice: number;
};

type KeychainOption = {
  id: number;
  label: string;
  category: string;
  stock_on_hand: number;
};

type SaleChannelOption = {
  id: number;
  code: string;
  label: string;
  is_active: boolean;
};

type EstimatedCostEntry = {
  code: string;
  requestedQty: number;
  availableQty: number;
  estimatedQty: number;
  unitCosts: number[];
  averageUnitCost: number;
  estimatedTotalCost: number;
};

type ScanSpecialItem = {
  id: number;
  product_code: string;
  group_key: "POT" | "FLOWER" | "PACKAGE";
  sort_order: number;
  is_active: boolean;
  product: Product;
};

type StockTransferLocation = "SHOP" | "MAIN" | "TIKTOK";
type StockTransferTarget = "TIKTOK" | "SHOP";

type StockShortageItem = {
  product_code: string;
  product_name: string;
  needed: number;
  tiktok_available: number;
  shortage: number;
  shop_available: number;
  main_available: number;
  transferSource: StockTransferLocation;
  transferStatus: "idle" | "loading" | "done" | "error";
};

const TOKEN_STORAGE_KEY = "bg_shop_token";

// ประเภทการชำระเงิน
type PaymentMethod = "เงินสด" | "สแกน" | "Alipay" | "WeChat" | "บัตรเดบิต";

function getDefaultSaleChannelCode(options: SaleChannelOption[]): string {
  const activeOptions = options.filter((option) => option.is_active);
  const source = activeOptions.length > 0 ? activeOptions : options;
  const mainShop = source.find((option) => option.code === "MAIN_SHOP");
  if (mainShop) return mainShop.code;
  return source[0]?.code ?? "MAIN_SHOP";
}

/** ==================== Autofocus & Camera helpers ==================== */
/** เปิด continuous autofocus (ถ้าอุปกรณ์รองรับ) */
async function enableContinuousAutofocus(track: MediaStreamTrack) {
  const caps: any = track.getCapabilities?.() || {};
  const modes: string[] = caps.focusMode || [];
  if (modes.includes("continuous")) {
    await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] } as any);
  } else if (modes.includes("auto")) {
    await track.applyConstraints({ advanced: [{ focusMode: "auto" }] } as any);
  }
}

/** สั่งโฟกัสใหม่ 1 ครั้ง แล้วกลับสู่ continuous/auto */
async function refocusOnce(track: MediaStreamTrack) {
  const caps: any = track.getCapabilities?.() || {};
  const modes: string[] = caps.focusMode || [];
  if (modes.includes("single-shot")) {
    await track.applyConstraints({ advanced: [{ focusMode: "single-shot" }] } as any);
    if (modes.includes("continuous")) {
      await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] } as any);
    } else if (modes.includes("auto")) {
      await track.applyConstraints({ advanced: [{ focusMode: "auto" }] } as any);
    }
  } else {
    // เครื่องไม่รองรับ: toggle ความละเอียดเล็กน้อยเพื่อบังคับโฟกัสใหม่
    const s = track.getSettings?.();
    const w = s?.width || 1280;
    await track.applyConstraints({ width: { ideal: w - 1 } } as any);
    await track.applyConstraints({ width: { ideal: w } } as any);
  }
}

/** แตะจอเพื่อโฟกัสตำแหน่ง (รองรับบางเบราว์เซอร์เท่านั้น) */
async function tapToFocus(
  track: MediaStreamTrack,
  videoEl: HTMLVideoElement,
  clientX: number,
  clientY: number
) {
  const rect = videoEl.getBoundingClientRect();
  const x = (clientX - rect.left) / rect.width;
  const y = (clientY - rect.top) / rect.height;
  try {
    await track.applyConstraints({
      advanced: [{ pointsOfInterest: [{ x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) }] }],
    } as any);
    await refocusOnce(track);
  } catch {
    // ไม่รองรับก็เงียบ ๆ
  }
}

/** ซูม (ถ้ากล้องรองรับ) */
async function trySetZoom(track: MediaStreamTrack, z: number) {
  const caps: any = track.getCapabilities?.() || {};
  if (caps.zoom) {
    const v = Math.max(caps.zoom.min, Math.min(caps.zoom.max, z));
    await track.applyConstraints({ advanced: [{ zoom: v }] } as any);
  }
}

/** ปรับชดเชยแสงทีละนิด (ถ้ากล้องรองรับ) */
async function bumpExposure(track: MediaStreamTrack, step = 0.3) {
  const caps: any = track.getCapabilities?.() || {};
  if (typeof caps.exposureCompensation?.min === "number") {
    const cur: any = track.getSettings?.() || {};
    const next = Math.max(
      caps.exposureCompensation.min,
      Math.min(caps.exposureCompensation.max, (cur.exposureCompensation ?? 0) + step)
    );
    await track.applyConstraints({ advanced: [{ exposureCompensation: next }] } as any);
  }
}

/** รอให้ <video> พร้อมก่อน (มีขนาดวิดีโอจริง) */
async function waitVideoReady(video: HTMLVideoElement): Promise<void> {
  if (video.readyState >= 2 && video.videoWidth > 0) return;
  await new Promise<void>((resolve) => {
    const cb = () => {
      if (video.videoWidth > 0) {
        video.removeEventListener("loadeddata", cb);
        resolve();
      }
    };
    video.addEventListener("loadeddata", cb);
  });
}

type DraftMeta = {
  id: number;
  created_at: string;
  draft_note: string;
  grand_total: number;
  item_count: number;
};

/** =========================================================== */

export default function ScanPage({
  allowedSaleChannels = [],
}: {
  allowedSaleChannels?: SaleChannelOption[];
}): JSX.Element {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [showExtraActions, setShowExtraActions] = useState(false);
  const [showCloverActions, setShowCloverActions] = useState(false);
  const [showFlowerActions, setShowFlowerActions] = useState(false);
  const [showKeychainPanel, setShowKeychainPanel] = useState(false);
  const [keychainItems, setKeychainItems] = useState<KC_Item[]>([]);
  const [keychainOptions, setKeychainOptions] = useState<KeychainOption[]>([]);
  const [keychainOptionsLoading, setKeychainOptionsLoading] = useState(false);
  const [keychainSelectedId, setKeychainSelectedId] = useState<number>(0);
  const [keychainQtyInput, setKeychainQtyInput] = useState<number>(1);
  const [keychainPriceInput, setKeychainPriceInput] = useState<number>(0);
  const [selectedTikTokPackageCode, setSelectedTikTokPackageCode] = useState("");
  const [selectedTikTokPackageFee, setSelectedTikTokPackageFee] = useState(0);
  // PMN-CRITICAL(/scan): TikTok Order No → orders.tiktok_order_no (digits only; required for TIKTOK_SHOP)
  const [tiktokOrderNo, setTiktokOrderNo] = useState("");
  const tiktokOrderNoInputRef = useRef<HTMLInputElement | null>(null);

  const CLOVER_CODES = ["pd539", "pd537", "pd538", "pd536"] as const;
  const CLOVER_NAME = ["phone chain - bright green","keychain - bright green", "phone chain - dark green",  "keychain - dark green"] as const;
  type CloverCode = typeof CLOVER_CODES[number];

  // 🔧 work canvas สำหรับ ROI & scale ladder
  const workCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const ensureCanvas = () => {
    if (!workCanvasRef.current) workCanvasRef.current = document.createElement("canvas");
    return workCanvasRef.current!;
  };

  // loop ควบคุมแอนิเมชันขณะ TRY_HARDER
  const scanLoopRef = useRef<number | null>(null);
  const gotOneRef = useRef<boolean>(false);

  function cloverBtnClass(name: string): string {
    if (name.includes("bright green")) {
      return "btn-clover-bright";
    }
    if (name.includes("dark green")) {
      return "btn-clover-dark";
    }
    return "btn-secondary";
  }

  // ---------- ZXing Readers (Fast + Hard) ----------
  const [readerFast] = useState(() => {
    const r = new BrowserMultiFormatReader();
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.QR_CODE,
      BarcodeFormat.EAN_13,
      BarcodeFormat.EAN_8,
      BarcodeFormat.CODE_128,
    ]);
    r.setHints?.(hints);
    return r;
  });

  const [readerHard] = useState(() => {
    const r = new BrowserMultiFormatReader();
    const hints = new Map();
    hints.set(DecodeHintType.TRY_HARDER, true);
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.QR_CODE,
      BarcodeFormat.EAN_13,
      BarcodeFormat.EAN_8,
      BarcodeFormat.CODE_128,
      BarcodeFormat.CODE_39,
      BarcodeFormat.ITF,
      BarcodeFormat.UPC_A,
      BarcodeFormat.UPC_E,
      BarcodeFormat.DATA_MATRIX,
      BarcodeFormat.PDF_417,
      BarcodeFormat.AZTEC,
    ]);
    r.setHints?.(hints);
    return r;
  });

  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState<string | undefined>(undefined);

  const [resultText, setResultText] = useState<string>("");
  const [errorMsg, setErrorMsg] = useState<string>("");

  const [saving, setSaving] = useState(false);
  const [saleChannel, setSaleChannel] = useState<string>("MAIN_SHOP");
  const [saleChannels, setSaleChannels] = useState<SaleChannelOption[]>([]);
  const [showProfitPreview, setShowProfitPreview] = useState(false);
  const [costPreviewLoading, setCostPreviewLoading] = useState(false);
  const [costPreviewError, setCostPreviewError] = useState<string>("");
  const [estimatedCostsByCode, setEstimatedCostsByCode] = useState<Map<string, EstimatedCostEntry>>(
    new Map()
  );

  // เลขบิลรันนิ่ง (เริ่ม 000)
  const [billSeq, setBillSeq] = useState(0);

  // ✅ หลายรายการ พร้อมจำนวน
  const [items, setItems] = useState<ItemEntry[]>([]);

  // ✅ เปิด/ปิดดีลแม่เหล็ก (PD350/PD351) — true = ใช้ดีล, false = ใช้ราคาเดิม
  const [enableMagnetPromo, setEnableMagnetPromo] = useState<boolean>(true);

  // ✅ ราคาต่อชิ้นแบบมีดีลพิเศษเฉพาะรหัส
  const getUnitPrice = (product: Product, qty: number): number => {
    // ถ้าปิดดีลพิเศษ → ใช้ราคาเดิมจากสินค้า
    if (!enableMagnetPromo) {
      return product.price;
    }

    // เปิดดีลพิเศษ → ใช้เงื่อนไขแม่เหล็ก
    if (product.code === "PD350" && qty > 1) {
      return 100; // แม่เหล็กใหญ่ >1 ชิ้น → 100/ชิ้น
    }
    if (product.code === "PD351" && qty > 1) {
      return 75; // แม่เหล็กเล็ก >1 ชิ้น → 75/ชิ้น
    }
    return product.price; // ตัวอื่น ๆ ใช้ราคาเดิม
  };

  // รวมยอดทั้งหมด
  const grandTotal = items.reduce(
    (sum, it) => sum + getUnitPrice(it.product, it.qty) * it.qty,
    0
  );
  const totalQty = items.reduce((sum, it) => sum + it.qty, 0);

  const isKeychainBill = keychainItems.length > 0;
  const keychainGrandTotal = keychainItems.reduce((s, it) => s + it.qty * it.unitPrice, 0);
  const keychainTotalQty = keychainItems.reduce((s, it) => s + it.qty, 0);
  const effectiveGrandTotal = isKeychainBill ? keychainGrandTotal : grandTotal;

  const [loadingProduct, setLoadingProduct] = useState<boolean>(false);
  const [showManual, setShowManual] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [toast, setToast] = useState<{ show: boolean; message: string }>({ show: false, message: "" });
  const [scanSpecialItems, setScanSpecialItems] = useState<ScanSpecialItem[]>([]);
  const [scanSpecialLoading, setScanSpecialLoading] = useState(false);
  const [scanSpecialError, setScanSpecialError] = useState("");

  const activeSaleChannels = saleChannels.filter((option) => option.is_active);
  const visibleSaleChannels = activeSaleChannels;
  const selectedSaleChannel =
    saleChannels.find((option) => option.code === saleChannel) ??
    visibleSaleChannels[0] ??
    null;
  const saleChannelLabel = selectedSaleChannel?.label ?? (saleChannel || "ยังไม่ได้กำหนด");
  const saleChannelTheme =
    saleChannel === "MAIN_SHOP"
      ? "main"
      : saleChannel === "BOOTH"
        ? "booth"
        : "custom";
  const estimatedCostEntries = items.map((entry) => {
    const estimated = estimatedCostsByCode.get(entry.product.code);
    return {
      entry,
      estimated: estimated ?? null,
    };
  });
  const estimatedCostTotal = estimatedCostEntries.reduce(
    (sum, row) => sum + Number(row.estimated?.estimatedTotalCost ?? 0),
    0
  );
  const estimatedGrossProfit = grandTotal - estimatedCostTotal;
  const hasEstimatedShortage = estimatedCostEntries.some(
    (row) =>
      row.estimated != null &&
      Number(row.estimated.estimatedQty ?? 0) < Number(row.entry.qty ?? 0)
  );
  const visibleScanSpecialItems = scanSpecialItems.filter((item) => item.is_active);
  const potSpecialItems = visibleScanSpecialItems.filter((item) => item.group_key === "POT");
  const flowerSpecialItems = visibleScanSpecialItems.filter((item) => item.group_key === "FLOWER");
  const packageSpecialItems = visibleScanSpecialItems.filter((item) => item.group_key === "PACKAGE");
  const isTikTokSale = saleChannel === "TIKTOK_SHOP";
  const isShopSale = saleChannel === "MAIN_SHOP";
  const selectedTikTokPackage =
    packageSpecialItems.find((item) => item.product_code === selectedTikTokPackageCode) ?? null;

  // ตัวเลือกไว้ทำปุ่มการ์ด
  const PAYMENT_OPTIONS: PaymentMethod[] = ["เงินสด", "สแกน", "Alipay", "WeChat", "บัตรเดบิต"];

  // เก็บวิธีชำระเงิน และสถานะ popup
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(true);

  // Transfer popup state
  const [showTransferPopup, setShowTransferPopup] = useState(false);
  const [transferTarget, setTransferTarget] = useState<StockTransferTarget>("TIKTOK");
  const [transferShortages, setTransferShortages] = useState<StockShortageItem[]>([]);
  const [transferSaveError, setTransferSaveError] = useState<string>("");
  const [transferSaving, setTransferSaving] = useState(false);
  const transferSourceOptions: StockTransferLocation[] =
    transferTarget === "TIKTOK" ? ["SHOP", "MAIN"] : ["TIKTOK", "MAIN"];
  const getAvailableAtLocation = (shortage: StockShortageItem, loc: StockTransferLocation): number => {
    if (loc === "SHOP") return shortage.shop_available;
    if (loc === "TIKTOK") return shortage.tiktok_available;
    return shortage.main_available;
  };

  // แคชสินค้า
  const [productsByCode, setProductsByCode] = useState<Map<string, Product>>(new Map());
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productsError, setProductsError] = useState<string | null>(null);

  const [discount, setDiscount] = useState<number>(0); // บาท
  const netTotal = Math.max(0, grandTotal - (discount || 0));
  const estimatedNetProfit = estimatedGrossProfit - discount;
  const effectiveNetTotal = Math.max(0, effectiveGrandTotal - (discount || 0));

  // modal ส่วนลด
  const [showDiscountPanel, setShowDiscountPanel] = useState(false);
  const [draftDiscount, setDraftDiscount] = useState<number>(0);
  const [discountErr, setDiscountErr] = useState<string>("");

  // เลือกไฟล์ภาพ (ถ่ายจากกล้อง/อัปโหลด)
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [imgPreview, setImgPreview] = useState<string | null>(null);

  // Draft system
  const [drafts, setDrafts] = useState<DraftMeta[]>([]);
  const [showDraftPanel, setShowDraftPanel] = useState(false);
  const [draftSaving, setDraftSaving] = useState(false);
  const [loadedDraftId, setLoadedDraftId] = useState<number | null>(null);

  const manualInputRef = useRef<HTMLInputElement | null>(null);

  // ---------- เสียงติ๊ดตอนสแกนสำเร็จ ----------
  const playBeep = () => {
    try {
      const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
      const ctx = new AudioCtx();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = 880;
      o.connect(g);
      g.connect(ctx.destination);
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.01);
      o.start();
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.15);
      o.stop(ctx.currentTime + 0.16);
    } catch {}
  };

  // โหลดรูปเป็น bitmap/image
  async function loadImageBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
    // ใช้ createImageBitmap ถ้ามี (เร็วกว่า)
    if ("createImageBitmap" in window) {
      return await createImageBitmap(file);
    }
    // fallback
    const url = URL.createObjectURL(file);
    try {
      const img = new Image();
      img.decoding = "async";
      img.src = url;
      await new Promise<void>((res, rej) => {
        img.onload = () => res();
        img.onerror = () => rej(new Error("โหลดรูปไม่สำเร็จ"));
      });
      return img;
    } finally {
      // ปล่อย URL ไว้จนกว่าจะวาดเสร็จ
    }
  }

  async function decodeFromImageFile(file: File) {
    setErrorMsg("");
    setResultText("");
    setImgPreview(URL.createObjectURL(file)); // แสดงพรีวิวเล็ก ๆ

    try {
      const bitmap = await loadImageBitmap(file);

      // ย่อภาพลงก่อน (กันภาพมือถือ 4K หนักเกิน)
      const maxSide = 1600; // ปรับได้
      const iw = "width" in bitmap ? (bitmap as any).width : (bitmap as HTMLImageElement).naturalWidth;
      const ih = "height" in bitmap ? (bitmap as any).height : (bitmap as HTMLImageElement).naturalHeight;
      const scale = Math.min(1, maxSide / Math.max(iw, ih));
      const cw = Math.max(1, Math.floor(iw * scale));
      const ch = Math.max(1, Math.floor(ih * scale));

      const canvas = ensureCanvas();
      canvas.width = cw;
      canvas.height = ch;
      const ctx = canvas.getContext("2d")!;
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, cw, ch);
      ctx.drawImage(bitmap as any, 0, 0, cw, ch);

      // ลองถอดรหัสหลายมุม (กัน EXIF/orientation)
      const tryAngles = [0, 90, 180, 270];
      let decoded: string | null = null;

      for (const ang of tryAngles) {
        if (ang !== 0) {
          // หมุนภาพบนแคนวาส
          const tmp = ensureCanvas();
          const w = (ang % 180 === 0) ? cw : ch;
          const h = (ang % 180 === 0) ? ch : cw;
          tmp.width = w; tmp.height = h;
          const c2 = tmp.getContext("2d")!;
          c2.fillStyle = "#fff";
          c2.fillRect(0, 0, w, h);
          c2.save();
          c2.translate(w / 2, h / 2);
          c2.rotate((ang * Math.PI) / 180);
          c2.drawImage(canvas, -cw / 2, -ch / 2);
          c2.restore();

          try {
            const res = await readerHard.decodeFromCanvas(tmp);
            decoded = res?.getText?.()?.trim() || null;
            if (decoded) break;
          } catch { /* try next angle */ }
        } else {
          try {
            const res = await readerHard.decodeFromCanvas(canvas);
            decoded = res?.getText?.()?.trim() || null;
            if (decoded) break;
          } catch { /* rotate next */ }
        }
      }

      if (!decoded) {
        setErrorMsg("อ่าน QR จากรูปไม่สำเร็จ — ลองถ่ายให้ชัด/สว่างขึ้น หรือครอปให้ใกล้ QR");
        return;
      }
      await handleDecodedCode(decoded);
    } catch (e: any) {
      setErrorMsg(e?.message ?? "ประมวลผลไฟล์ภาพไม่สำเร็จ");
    }
  }

  // ---------- โหลดรายชื่อกล้อง ----------
  useEffect(() => {
    (async () => {
      try {
        const list = await BrowserMultiFormatReader.listVideoInputDevices();
        setDevices(list);
        // ดีฟอลต์ใช้กล้องหลังถ้ามี (ส่วนใหญ่ label มีคำว่า back / environment)
        const back = list.find((d) => /back|environment/i.test(d.label || ""));
        if (!deviceId && list.length) setDeviceId((back ?? list[list.length - 1]).deviceId);
      } catch (e: any) {
        setErrorMsg(e?.message ?? "ไม่สามารถอ่านรายชื่อกล้องได้");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // auto-hide toast
  useEffect(() => {
    if (!toast.show) return;
    const t = setTimeout(() => setToast({ show: false, message: "" }), 2000);
    return () => clearTimeout(t);
  }, [toast.show]);

  // ---------- โหลดสินค้าตอนเปิดหน้า ----------
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoadingProducts(true);
        setProductsError(null);

        const resp = await fetch(apiUrl("/products"));
        const json = await resp.json();
        if (!resp.ok) throw new Error(json?.error ?? resp.statusText);

        const list: Product[] = (json.items ?? []).map((r: any) => ({
          code: r.code,
          name: r.name,
          price: Number(r.price ?? 0),
          unit: r.unit ?? undefined,
        }));

        if (!alive) return;
        const map = new Map<string, Product>();
        for (const p of list) {
          map.set(p.code, p);
          map.set(p.code.toLowerCase(), p);
        }
        setProductsByCode(map);
      } catch (e: any) {
        if (!alive) return;
        setProductsError(e?.message ?? "ดึงรายการสินค้าไม่สำเร็จ");
      } finally {
        if (alive) setLoadingProducts(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const normalized =
      allowedSaleChannels.length > 0
        ? allowedSaleChannels.filter(
            (channel) => channel && channel.is_active && channel.code && channel.label
          )
        : [];

    setSaleChannels(normalized);
    setSaleChannel((prev) => {
      if (normalized.length === 0) {
        return "";
      }

      const exists = normalized.some((option) => option.code === prev);
      return exists ? prev : getDefaultSaleChannelCode(normalized);
    });
  }, [allowedSaleChannels]);

  useEffect(() => {
    if (!showProfitPreview) return;

    if (items.length === 0) {
      setEstimatedCostsByCode(new Map());
      setCostPreviewError("");
      return;
    }

    let cancelled = false;

    const loadEstimatedCosts = async () => {
      try {
        setCostPreviewLoading(true);
        setCostPreviewError("");

        const payload = {
          items: items.map((entry) => ({
            code: entry.product.code,
            qty: entry.qty,
          })),
        };

        const resp = await fetch(apiUrl("/products/costs"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const json = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(json?.error ?? resp.statusText);

        const next = new Map<string, EstimatedCostEntry>();
        for (const row of Array.isArray(json?.items) ? json.items : []) {
          const code = String(row?.code ?? "").trim();
          if (!code) continue;
          next.set(code, {
            code,
            requestedQty: Number(row?.requestedQty ?? 0),
            availableQty: Number(row?.availableQty ?? 0),
            estimatedQty: Number(row?.estimatedQty ?? 0),
            unitCosts: Array.isArray(row?.unitCosts)
              ? row.unitCosts.map((cost: unknown) => Number(cost ?? 0))
              : [],
            averageUnitCost: Number(row?.averageUnitCost ?? 0),
            estimatedTotalCost: Number(row?.estimatedTotalCost ?? 0),
          });
        }

        if (!cancelled) {
          setEstimatedCostsByCode(next);
        }
      } catch (e: any) {
        if (!cancelled) {
          setEstimatedCostsByCode(new Map());
          setCostPreviewError(e?.message ?? "โหลดต้นทุนประมาณการไม่สำเร็จ");
        }
      } finally {
        if (!cancelled) {
          setCostPreviewLoading(false);
        }
      }
    };

    loadEstimatedCosts();

    return () => {
      cancelled = true;
    };
  }, [showProfitPreview, items]);

  useEffect(() => {
    if (!showFlowerActions && !isTikTokSale) return;
    if (scanSpecialItems.length > 0) return;

    let cancelled = false;

    const loadScanSpecialItems = async () => {
      try {
        setScanSpecialLoading(true);
        setScanSpecialError("");

        const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
        const resp = await fetch(apiUrl("/scan-special-items"), {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok || !Array.isArray(data?.items)) {
          throw new Error(data?.error ?? "failed_to_fetch_scan_special_items");
        }

        const nextItems = data.items
          .map((row: any) => {
            const product = row?.product;
            const code = typeof row?.product_code === "string" ? row.product_code.trim() : "";
            const groupKey = typeof row?.group_key === "string" ? row.group_key.trim().toUpperCase() : "";
            const name = typeof product?.name === "string" ? product.name.trim() : "";
            const price = Number(product?.price ?? 0);
            const estimatedUnitCostRaw = product?.estimated_unit_cost;
            const availableQtyRaw = product?.available_qty;

            if (!code || !name) return null;
            if (groupKey !== "POT" && groupKey !== "FLOWER" && groupKey !== "PACKAGE") return null;

            return {
              id: Number(row?.id ?? 0),
              product_code: code,
              group_key: groupKey as "POT" | "FLOWER" | "PACKAGE",
              sort_order: Number(row?.sort_order ?? 0),
              is_active: Boolean(row?.is_active),
              product: {
                code,
                name,
                price: Number.isFinite(price) ? price : 0,
                unit: typeof product?.unit === "string" && product.unit.trim() ? product.unit.trim() : undefined,
                estimated_unit_cost:
                  estimatedUnitCostRaw == null || !Number.isFinite(Number(estimatedUnitCostRaw))
                    ? null
                    : Number(estimatedUnitCostRaw),
                available_qty: Number.isFinite(Number(availableQtyRaw)) ? Number(availableQtyRaw) : 0,
              },
            } satisfies ScanSpecialItem;
          })
          .filter((item: ScanSpecialItem | null): item is ScanSpecialItem => item != null)
          .sort((a: ScanSpecialItem, b: ScanSpecialItem) => {
            if (a.group_key !== b.group_key) return a.group_key.localeCompare(b.group_key);
            if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
            return a.id - b.id;
          });

        if (!cancelled) {
          setScanSpecialItems(nextItems);
        }
      } catch (err: any) {
        if (!cancelled) {
          setScanSpecialError(err?.message === "missing_auth_token"
            ? "กรุณาเข้าสู่ระบบใหม่ก่อนโหลดปุ่มลัด"
            : "โหลดรายการปุ่มลัดไม่สำเร็จ");
        }
      } finally {
        if (!cancelled) {
          setScanSpecialLoading(false);
        }
      }
    };

    void loadScanSpecialItems();

    return () => {
      cancelled = true;
    };
  }, [isTikTokSale, showFlowerActions, scanSpecialItems.length]);

  useEffect(() => { void fetchDrafts(); }, []);

  // PMN-CRITICAL(/scan): scan/manual add both depend on this cache-first lookup before falling back to GET /products?q=code.
  const fetchProduct = async (code: string): Promise<Product | null> => {
    setLoadingProduct(true);
    setErrorMsg("");
    try {
      const key = code.trim();
      const cached = productsByCode.get(key) ?? productsByCode.get(key.toLowerCase());
      if (cached) return cached;

      const url = new URL(apiUrl("/products"));
      url.searchParams.set("q", code);
      const res = await fetch(url.toString(), { headers: { Accept: "application/json" } });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) throw new Error(data?.error ?? res.statusText);

      const items = Array.isArray(data?.items) ? data.items : [];
      const found =
        items.find((it: any) => String(it.code).toLowerCase() === key.toLowerCase()) ?? items[0];
      if (!found) return null;

      const p: Product = {
        code: found.code,
        name: found.name,
        price: Number(found.price ?? 0),
        unit: found.unit ?? undefined,
      };

      setProductsByCode((prev) => {
        const next = new Map(prev);
        next.set(p.code, p);
        next.set(p.code.toLowerCase(), p);
        return next;
      });

      return p;
    } catch (e: any) {
      setErrorMsg(e?.message ?? "ดึงข้อมูลสินค้าไม่สำเร็จ");
      return null;
    } finally {
      setLoadingProduct(false);
    }
  };

  // PMN-CRITICAL(/scan): manual entry must mirror scan entry and use the backend canonical product code for item merging.
  const addProductByCode = async (rawCode: string) => {
    const input = rawCode.trim();
    if (!input) return;
  
    setResultText(input);
    playBeep();
  
    const p = await fetchProduct(input);
    if (!p) {
      setErrorMsg(`ไม่พบสินค้า code: ${input}`);
      return;
    }
  
    const canonicalCode = p.code; // ✅ ใช้โค้ดจริงจากระบบ เช่น PD539
  
    let toastName = p.name;
    let toastQty = 1;
  
    setItems((prev) => {
      const idx = prev.findIndex((it) => it.product.code === canonicalCode);
      if (idx >= 0) {
        const copy = [...prev];
        const newQty = copy[idx].qty + 1;
        copy[idx] = { ...copy[idx], qty: newQty };
        toastName = copy[idx].product.name;
        toastQty = newQty;
        return copy;
      }
      return [{ product: p, qty: 1 }, ...prev];
    });
  
    setToast({ show: true, message: `เพิ่ม ${toastName} x${toastQty}` });
  };

  const chooseTikTokPackage = (item: ScanSpecialItem) => {
    const availableQty = Math.max(0, Number(item.product.available_qty ?? 0));
    if (availableQty <= 0) return;

    setSelectedTikTokPackageCode(item.product_code);
    setSelectedTikTokPackageFee(Number(item.product.estimated_unit_cost ?? 0));
  };

  const clearTikTokPackage = () => {
    setSelectedTikTokPackageCode("");
    setSelectedTikTokPackageFee(0);
  };

  const focusTikTokOrderNo = () => {
    const el = tiktokOrderNoInputRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.focus();
  };

  // PMN-CRITICAL(/scan): shared by /sales_save (normal + transfer popup) and /sales_save_keychain.
  // 409 tiktok_order_no_duplicate → confirm → resend the SAME payload (same bill no) with confirmDuplicateTiktokOrderNo.
  const postSaleWithDupConfirm = async (path: string, payload: Record<string, unknown>) => {
    const post = async (body: Record<string, unknown>) => {
      const resp = await fetch(apiUrl(path), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await resp.json().catch(() => ({}));
      return { resp, data };
    };
    const first = await post(payload);
    if (first.resp.status !== 409 || first.data?.error !== "tiktok_order_no_duplicate") {
      return { ...first, cancelled: false };
    }
    const bills = Array.isArray(first.data?.bills)
      ? first.data.bills.map((b: unknown) => `#${b}`).join(", ")
      : "-";
    const ok = window.confirm(
      `TikTok Order No ${first.data?.tiktok_order_no ?? ""} ถูกใช้แล้วในบิล ${bills}\n\nยืนยันบันทึกเลขซ้ำหรือไม่? (เช่น order เดียวแยกหลายบิล)`
    );
    if (!ok) return { ...first, cancelled: true };
    const second = await post({ ...payload, confirmDuplicateTiktokOrderNo: true });
    return { ...second, cancelled: false };
  };

  const describeSaleSaveError = (data: any, fallback: string) => {
    if (data?.error === "tiktok_order_no_required") return "กรุณากรอก TikTok Order No";
    if (data?.error === "invalid_tiktok_order_no") return "TikTok Order No ต้องเป็นตัวเลขเท่านั้น (10–30 หลัก)";
    if (data?.error === "tiktok_order_no_duplicate") return "TikTok Order No ซ้ำกับบิลอื่น";
    return data?.error ?? fallback;
  };
  
  function resetUIAfterSave(bill: string) {
    // auto-delete loaded draft (fire-and-forget)
    if (loadedDraftId !== null) {
      fetch(apiUrl(`/scan-drafts/${loadedDraftId}`), { method: "DELETE" })
        .then(() => setDrafts(prev => prev.filter(d => d.id !== loadedDraftId)))
        .catch(() => alert("ลบดราฟไม่สำเร็จ กรุณาลบเองจาก 📂 ดราฟ"));
      setLoadedDraftId(null);
    }
    setItems([]);
    setKeychainItems([]);
    setShowKeychainPanel(false);
    setResultText("");
    setErrorMsg("");
    setDiscount(0);
    setSelectedTikTokPackageCode("");
    setSelectedTikTokPackageFee(0);
    setTiktokOrderNo("");
    setShowProfitPreview(false);
    setEstimatedCostsByCode(new Map());
    setCostPreviewError("");
    setShowManual(false);
    setScanning(false);
    setToast({ show: true, message: `บันทึกบิล ${bill} เรียบร้อย` });

    stopScanLoop();
    stopStream();

    setBillSeq(Number(bill) + 1);
  }

  const fetchDrafts = async () => {
    try {
      const r = await fetch(apiUrl("/scan-drafts"));
      const j = await r.json();
      setDrafts(j.drafts ?? []);
    } catch {}
  };

  const saveDraft = async () => {
    if (items.length === 0 && keychainItems.length === 0) {
      alert("ไม่มีสินค้าในบิล"); return;
    }
    setDraftSaving(true);
    try {
      const note = items.length > 0
        ? items.slice(0, 2).map(i => `${i.product.code}x${i.qty}`).join(", ") + (items.length > 2 ? "..." : "")
        : keychainItems.slice(0, 2).map((i: any) => `${i.label}x${i.qty}`).join(", ");
      const totalQty = items.reduce((s, i) => s + (i.qty || 1), 0)
        + keychainItems.reduce((s: number, i: any) => s + (i.qty || 1), 0);
      await fetch(apiUrl("/scan-drafts"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items,
          keychainItems,
          discount,
          paymentMethod,
          saleChannel,
          tiktokPackageCode: selectedTikTokPackageCode,
          tiktokPackageFee: selectedTikTokPackageFee,
          tiktokOrderNo,
          grandTotal: effectiveGrandTotal,
          itemCount: totalQty,
          draftNote: note,
        }),
      });
      await fetchDrafts();
      setToast({ show: true, message: "บันทึกดราฟสำเร็จ" });
    } catch {
      alert("บันทึกดราฟไม่สำเร็จ");
    } finally {
      setDraftSaving(false);
    }
  };

  const loadDraft = async (id: number) => {
    if (items.length > 0 || keychainItems.length > 0) {
      if (!window.confirm("มีสินค้าในบิลอยู่แล้ว ต้องการโหลดดราฟทับหรือไม่?")) return;
    }
    try {
      const r = await fetch(apiUrl(`/scan-drafts/${id}`));
      const d = await r.json();
      setItems(d.items ?? []);
      setKeychainItems(d.keychain_items ?? []);
      setDiscount(d.discount ?? 0);
      if (d.payment_method) setPaymentMethod(d.payment_method as any);
      setSaleChannel(d.sale_channel ?? "MAIN_SHOP");
      setSelectedTikTokPackageCode(d.tiktok_package_code ?? "");
      setSelectedTikTokPackageFee(d.tiktok_package_fee ?? 0);
      setTiktokOrderNo(String(d.tiktok_order_no ?? "").replace(/\D/g, "").slice(0, 30));
      setLoadedDraftId(id);
      setShowDraftPanel(false);
    } catch {
      alert("โหลดดราฟไม่สำเร็จ");
    }
  };

  const deleteDraft = async (id: number) => {
    try {
      await fetch(apiUrl(`/scan-drafts/${id}`), { method: "DELETE" });
      setDrafts(prev => prev.filter(d => d.id !== id));
      if (loadedDraftId === id) setLoadedDraftId(null);
    } catch {
      alert("ลบดราฟไม่สำเร็จ");
    }
  };

  // ---------- กล้อง: ควบคุม loop & stream ----------
  // PMN-CRITICAL(/scan): always reset animation/got-one guard when stopping, or the next scan can duplicate or never fire.
  const stopScanLoop = () => {
    if (scanLoopRef.current) cancelAnimationFrame(scanLoopRef.current);
    scanLoopRef.current = null;
    gotOneRef.current = false;
  };

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  // ---------- วาด ROI ลง canvas ----------
  const drawROIToCanvas = (video: HTMLVideoElement, scale = 1) => {
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) return null;
    const roiPct = { w: 0.8, h: 0.8 };
    const rw = vw * roiPct.w;
    const rh = vh * roiPct.h;
    const rx = (vw - rw) / 2;
    const ry = (vh - rh) / 2;
    const canvas = ensureCanvas();
    canvas.width = Math.max(1, Math.floor(rw * scale));
    canvas.height = Math.max(1, Math.floor(rh * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(video, rx, ry, rw, rh, 0, 0, canvas.width, canvas.height);
    return canvas;
  };

  // ---------- เฟสเร็ว (decode จาก <video> ตรง ๆ) ----------
  // PMN-NOTE(/scan): fast phase is intentionally timeboxed before the heavier ROI/scale ladder begins.
  async function fastPhase(video: HTMLVideoElement): Promise<string | null> {
    return new Promise<string | null>((resolve) => {
      let gotOne = false;
  
      // คืนค่าเป็น Promise<IScannerControls>
      const controlsPromise = readerFast.decodeFromVideoElement(
        video,
        (result: Result | undefined /* or null */, _err?: unknown) => {
          if (gotOne) return;
          if (result) {
            gotOne = true;
            // ต้อง stop ผ่าน then ของพรอมิส
            controlsPromise.then((c) => c.stop()).catch(() => {});
            resolve(result.getText().trim());
          }
        }
      );
  
      // timebox เฟสเร็ว ~800ms
      setTimeout(() => {
        if (!gotOne) {
          controlsPromise.then((c) => c.stop()).catch(() => {});
          resolve(null);
        }
      }, 800);
    });
  }
  

  // ---------- จัดการเมื่อได้โค้ด ----------
  // PMN-CRITICAL(/scan): decoded codes enter the same item/qty path as manual input, then stop camera resources immediately.
  const handleDecodedCode = async (code: string) => {
    if (gotOneRef.current) return;
    gotOneRef.current = true;
  
    const input = code.trim();
    setResultText(input);
    playBeep();
  
    const p = await fetchProduct(input);
    if (!p) {
      setErrorMsg(`ไม่พบสินค้า code: ${input}`);
      setScanning(false);
      stopScanLoop();
      stopStream();
      return;
    }
  
    const canonicalCode = p.code; // ✅
  
    let toastName = p.name;
    let toastQty = 1;
  
    setItems((prev) => {
      const idx = prev.findIndex((it) => it.product.code === canonicalCode);
      if (idx >= 0) {
        const copy = [...prev];
        const newQty = copy[idx].qty + 1;
        copy[idx] = { ...copy[idx], qty: newQty };
        toastName = copy[idx].product.name;
        toastQty = newQty;
        return copy;
      }
      return [{ product: p, qty: 1 }, ...prev];
    });
  
    setToast({ show: true, message: `เพิ่ม ${toastName} x${toastQty}` });
  
    setScanning(false);
    stopScanLoop();
    stopStream();
  };
  

  // ---------- วน TRY_HARDER ด้วย ROI + scale ladder + zoom/exposure ----------
  // PMN-CRITICAL(/scan): hard scan loop owns the ROI/scale ladder and must respect scanning + gotOneRef to avoid duplicate adds.
  const startScanLoop = () => {
    const video = videoRef.current!;
    const track = streamRef.current?.getVideoTracks?.()[0];
    const hardScales = [1, 1.5, 2];
    let zoom = 1;
    let lastAdj = performance.now();

    const hardStep = async () => {
      if (!scanning || gotOneRef.current) return;

      // ลองหลายสเกลบน ROI
      let decoded: string | null = null;
      for (const s of hardScales) {
        const canvas = drawROIToCanvas(video, s);
        if (!canvas) continue;
        try {
          const res = await readerHard.decodeFromCanvas(canvas);
          const txt = res?.getText?.();
          if (txt) {
            decoded = txt.trim();
            break;
          }
        } catch {}
      }

      if (decoded) {
        await handleDecodedCode(decoded);
        return;
      }

      // ค่อย ๆ เพิ่มความสว่าง/ซูม เพื่อช่วยอ่าน
      const now = performance.now();
      if (track && now - lastAdj > 700) {
        lastAdj = now;
        await bumpExposure(track, 0.3).catch(() => {});
        zoom = Math.min(zoom + 0.2, 2.0);
        await trySetZoom(track, zoom).catch(() => {});
      }
      scanLoopRef.current = requestAnimationFrame(hardStep);
    };

    (async () => {
      // เฟสเร็ว
      const quick = await fastPhase(video);
      if (quick) {
        await handleDecodedCode(quick);
        return;
      }
      // เฟสยาก
      scanLoopRef.current = requestAnimationFrame(hardStep);
    })();
  };

  // ---------- เริ่ม/หยุดกล้อง + วงจรสแกน ----------
  // PMN-CRITICAL(/scan): camera lifecycle must pair getUserMedia/startScanLoop with stopScanLoop/stopStream cleanup.
  useEffect(() => {
    if (!videoRef.current) return;

    if (!scanning) {
      stopScanLoop();
      stopStream();
      return;
    }

    (async () => {
      try {
        setErrorMsg("");
        setResultText("");
        gotOneRef.current = false;

        const constraints: MediaStreamConstraints = {
          video: deviceId
            ? { deviceId: { exact: deviceId }, width: { ideal: 1280, min: 640 }, height: { ideal: 720, min: 480 } }
            : { facingMode: { ideal: "environment" }, width: { ideal: 1280, min: 640 }, height: { ideal: 720, min: 480 } },
          audio: false,
        };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        videoRef.current!.srcObject = stream;
        streamRef.current = stream;
        await videoRef.current!.play();
        await waitVideoReady(videoRef.current!);

        const track = stream.getVideoTracks()[0];
        enableContinuousAutofocus(track).catch(() => {});

        startScanLoop();
      } catch (e: any) {
        setErrorMsg(e?.message ?? "เปิดกล้องไม่ได้ (ต้อง https หรือ localhost)");
        setScanning(false);
      }
    })();

    return () => {
      stopScanLoop();
      stopStream();
    };
  }, [deviceId, scanning]);

  // ---------- ไฟฉาย ----------
  const toggleTorch = async (on: boolean) => {
    try {
      const track = streamRef.current?.getVideoTracks?.()[0];
      if (!track) {
        setErrorMsg("ไม่พบวิดีโอแทร็ก");
        return;
      }
      const caps = (track.getCapabilities?.() as any) as { torch?: boolean };
      if (caps?.torch) {
        await track.applyConstraints({ advanced: [{ torch: on }] } as any);
      } else {
        setErrorMsg("อุปกรณ์นี้ไม่รองรับไฟฉาย");
      }
    } catch {
      setErrorMsg("เปิดไฟฉายไม่สำเร็จ");
    }
  };

  const stopScan = () => {
    setScanning(false);
    stopScanLoop();
    stopStream();
  };

  // ---------- เปลี่ยนจำนวนด้วย input ----------
  const setQty = (index: number, value: number) => {
    setItems((prev) => {
      const copy = [...prev];
      const safe = Number.isFinite(value) ? Math.floor(value) : 0;
      // อนุญาตให้เป็น 0 ชั่วคราวได้ แต่ไม่ให้ติดลบ
      copy[index] = { ...copy[index], qty: Math.max(0, safe) };
      return copy;
    });
  };
  const removeItem = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };
  const clearItems = () => setItems([]);

  return (
    <div
      className={`min-vh-100 d-flex align-items-center justify-content-center ${
        saleChannelTheme === "main"
          ? "scan-page-shell scan-page-shell-main"
          : saleChannelTheme === "booth"
            ? "scan-page-shell scan-page-shell-booth"
            : "scan-page-shell scan-page-shell-custom"
      }`}
    >
      {loadingProducts && (
        <div className="d-flex align-items-center gap-2 mb-2">
          <div className="spinner-border spinner-border-sm" /> <span>กำลังโหลดรายการสินค้า…</span>
        </div>
      )}
      {productsError && (
        <div className="alert alert-warning rounded-3 mb-2">โหลดสินค้าไม่สำเร็จ: {productsError}</div>
      )}

      <div className="container py-4 py-md-5">
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 col-lg-6">
            <div
              className={`card shadow border-0 rounded-4 ${
                saleChannelTheme === "main"
                  ? "scan-page-card scan-page-card-main"
                  : saleChannelTheme === "booth"
                    ? "scan-page-card scan-page-card-booth"
                    : "scan-page-card scan-page-card-custom"
              }`}
            >
              <div className="card-body p-4 p-md-5">
                <div className="d-flex justify-content-end mb-3">
                  <button
                    type="button"
                    className="btn btn-outline-dark flex-shrink-0 scan-profit-toggle-btn"
                    aria-expanded={showProfitPreview}
                    onClick={() => setShowProfitPreview((prev) => !prev)}
                    disabled={items.length === 0}
                  >
                    {showProfitPreview ? "ซ่อนประมาณการกำไร" : "ดูประมาณการกำไร"}
                  </button>
                </div>

                {showProfitPreview && (
                  <div className="card border-0 shadow-sm rounded-4 mb-3">
                    <div className="card-body p-3 p-md-4">
                      <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                        <div>
                          <div className="fw-bold text-dark">ประมาณการกำไรก่อนบันทึก</div>
                          <div className="small text-muted">
                            คำนวณจากต้นทุน stock คงเหลือแบบ FIFO ณ ตอนนี้
                          </div>
                        </div>
                        {costPreviewLoading && (
                          <div className="small text-primary d-flex align-items-center gap-2">
                            <div className="spinner-border spinner-border-sm" />
                            <span>กำลังคำนวณ…</span>
                          </div>
                        )}
                      </div>

                      {costPreviewError && (
                        <div className="alert alert-warning rounded-3 py-2 px-3 mb-3">
                          โหลดต้นทุนประมาณการไม่สำเร็จ: {costPreviewError}
                        </div>
                      )}

                      {hasEstimatedShortage && (
                        <div className="alert alert-warning rounded-3 py-2 px-3 mb-3">
                          บางรายการมี stock คงเหลือไม่พอสำหรับประมาณการ ต้นทุนที่แสดงอาจยังไม่ครบตามจำนวนในบิล
                        </div>
                      )}

                      <div className="row g-2 mb-3">
                        <div className="col-12 col-md-6">
                          <div className="border rounded-3 p-2 h-100 bg-light">
                            <div className="small text-muted mb-1">ต้นทุนรวมประมาณการ</div>
                            <div className="fs-5 fw-bold text-dark lh-sm">
                              {estimatedCostTotal.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                              บาท
                            </div>
                          </div>
                        </div>
                        <div className="col-12 col-md-6">
                          <div className="border rounded-3 p-2 h-100 bg-light">
                            <div className="small text-muted mb-1">กำไรขั้นต้นประมาณการ</div>
                            <div className="fs-5 fw-bold text-dark lh-sm">
                              {estimatedGrossProfit.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                              บาท
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="border rounded-3 p-3 mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="text-muted">ส่วนลด</span>
                          <span className="fw-semibold">
                            -{" "}
                            {discount.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}{" "}
                            บาท
                          </span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fw-semibold">กำไรสุทธิประมาณการ</span>
                          <span className="fs-5 fw-bold text-success">
                            {estimatedNetProfit.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}{" "}
                            บาท
                          </span>
                        </div>
                      </div>

                      <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                          <thead>
                            <tr>
                              <th>รหัส</th>
                              <th>จำนวน</th>
                              <th>ต้นทุน/หน่วย</th>
                              <th>ต้นทุนรวม</th>
                            </tr>
                          </thead>
                          <tbody>
                            {estimatedCostEntries.map(({ entry, estimated }) => (
                              <tr key={`profit-preview-${entry.product.code}`}>
                                <td>
                                  <div className="fw-semibold">{entry.product.code}</div>
                                  <div className="small text-muted">{entry.product.name}</div>
                                </td>
                                <td>{entry.qty}</td>
                                <td>
                                  {Number(estimated?.averageUnitCost ?? 0).toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                  {estimated && estimated.estimatedQty < entry.qty && (
                                    <div className="small text-warning">
                                      คำนวณได้ {estimated.estimatedQty}/{entry.qty} ชิ้น
                                    </div>
                                  )}
                                </td>
                                <td>
                                  {Number(estimated?.estimatedTotalCost ?? 0).toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                <h4 className="mb-3 text-primary text-center">สแกนบันทึกสินค้าขาย</h4>

                {/* Toast แจ้งเตือนมุมขวาบน */}
                <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1080 }}>
                  {toast.show && (
                    <div className="alert alert-success shadow mb-0 rounded-3 py-2 px-3">{toast.message}</div>
                  )}
                </div>

                {/* Popup เลือกวิธีชำระเงิน */}
                {showPaymentModal && (
                  <div
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 2000 }}
                  >
                    <div className="card shadow-lg border-0 rounded-4" style={{ maxWidth: 720, width: "92%" }}>
                      <div className="card-body p-4">
                        <h5 className="mb-3 text-center">เลือกวิธีชำระเงิน</h5>
                        <div className="row row-cols-2 g-3">
                          {PAYMENT_OPTIONS.map((opt) => (
                            <div className="col" key={opt}>
                              <button
                                type="button"
                                className={`card w-100 h-100 border-2 rounded-4 text-decoration-none bg-secondary text-black ${
                                  paymentMethod === opt ? "border-primary" : "border-light"
                                }`}
                                onClick={() => {
                                  setPaymentMethod(opt);
                                  setShowPaymentModal(false);
                                }}
                              >
                                <div
                                  className="card-body d-flex align-items-center justify-content-center"
                                  style={{ minHeight: 100 }}
                                >
                                  <div className="text-center">
                                    <div className="fw-bold">{opt}</div>
                                    {paymentMethod === opt && (
                                      <div className="small text-primary mt-1">✓ เลือกแล้ว</div>
                                    )}
                                  </div>
                                </div>
                              </button>
                            </div>
                          ))}
                        </div>

                        <div className="text-center mt-3">
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => setShowPaymentModal(false)}
                          >
                            ข้ามไปก่อน
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* โซนกล้อง */}
                <div className="ratio ratio-4x3 bg-secondary rounded-3 mb-3 d-flex align-items-center justify-content-center">
                  <video
                    ref={videoRef}
                    className="w-100 h-100 rounded-3"
                    muted
                    playsInline
                    autoPlay
                    onClick={(e) => {
                      const track = streamRef.current?.getVideoTracks?.()[0];
                      if (!track || !videoRef.current) return;
                      tapToFocus(track, videoRef.current, e.clientX, e.clientY).catch(() => {});
                    }}
                  />
                </div>

                {/* เลือกกล้อง + ไฟ + โฟกัสใหม่ */}
                <div className="row gy-2 align-items-center mb-3">
                  <div className="col-12 col-md-6">
                    <select
                      className="form-select"
                      value={deviceId ?? ""}
                      onChange={(e) => setDeviceId(e.target.value)}
                    >
                      {devices.map((d, idx) => (
                        <option key={d.deviceId || idx} value={d.deviceId}>
                          {d.label || `Camera ${idx + 1}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-6 d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary flex-fill"
                      onClick={() => toggleTorch(true)}
                    >
                      เปิดไฟ
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary flex-fill"
                      onClick={() => toggleTorch(false)}
                    >
                      ปิดไฟ
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-primary flex-fill"
                      onClick={async () => {
                        const track = streamRef.current?.getVideoTracks?.()[0];
                        if (!track) {
                          setErrorMsg("ยังไม่พบกล้อง/สตรีม");
                          return;
                        }
                        await refocusOnce(track).catch(() => {});
                      }}
                    >
                      โฟกัสใหม่
                    </button>
                  </div>
                </div>

                {/* ปุ่ม: สแกน 1 ครั้ง (ด้วยเฟสเร็ว แล้วตกชั้น TRY_HARDER อัตโนมัติ) */}
                <div className="d-grid gap-2 mb-3">
                  <button
                    type="button"
                    className="btn btn-success btn-lg"
                    disabled={scanning}
                    onClick={() => setScanning(true)}
                  >
                    {scanning ? "กำลังสแกน..." : "▶️ สแกน 1 ครั้ง"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={stopScan}
                  >
                    🛑 หยุดกล้อง
                  </button>
                </div>

                {/* สถานะ / โค้ดล่าสุด */}
                {resultText && (
                  <div className="alert alert-info rounded-3 py-2 mb-3" role="alert">
                    โค้ดล่าสุด: <span className="fw-semibold">{resultText}</span>
                  </div>
                )}
                {loadingProduct && (
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <div className="spinner-border spinner-border-sm" role="status" />
                    <span>กำลังดึงข้อมูลสินค้า…</span>
                  </div>
                )}

                {/* กรอกมือสำรอง */}
                <div className="mt-3">
                  <button
                    type="button"
                    className="btn btn-product-code w-100"
                    aria-expanded={showManual}
                    onClick={() => setShowManual((s) => !s)}
                  >
                          {showManual ? "ซ่อน PRODUCT CODE" : "PRODUCT CODE"}
                  </button>

                  {showManual && (
                    <div className="border rounded-3 p-3 mt-2 bg-white">
                      <form
                        className="row gy-3"
                        onSubmit={async (e) => {
                          e.preventDefault();
                          const code = manualInputRef.current?.value ?? "";
                          await addProductByCode(code);
                          if (manualInputRef.current) manualInputRef.current.value = "";
                        }}
                      >
                        <div className="col-12">
                          <label htmlFor="manualCode" className="form-label">
                            รหัส (พิมพ์แทนการสแกน)
                          </label>
                          <input
                            id="manualCode"
                            type="text"
                            className="form-control"
                            placeholder="dc025 / 885... / ข้อความ"
                            ref={manualInputRef}
                          />
                        </div>
                        <div className="col-12 d-flex gap-2">
                          <button type="submit" className="btn btn-primary flex-fill">
                            เพิ่มรายการ
                          </button>
                          <button type="button" className="btn btn-outline-secondary flex-fill" onClick={stopScan}>
                            หยุดกล้อง
                          </button>
                          <button type="button" className="btn btn-outline-primary flex-fill" onClick={clearItems}>
                            ล้างรายการ
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                </div>

                {/* ปุ่มใหม่: กดแล้วโชว์ปุ่มไอคอน 2 ปุ่ม */}
                <div className="d-flex gap-3 mt-3 mb-2">
                  <button
                    type="button"
                    className={`btn rounded-circle d-inline-flex align-items-center justify-content-center ${
                      showExtraActions ? "btn-primary" : "btn-outline-primary"
                    }`}
                    style={{ width: 60, height: 60 }}
                    aria-label={showExtraActions ? "ซ่อนปุ่มเพิ่มแม่เหล็ก" : "แสดงปุ่มเพิ่มแม่เหล็ก"}
                    title={showExtraActions ? "ซ่อนปุ่มเพิ่มแม่เหล็ก" : "แสดงปุ่มเพิ่มแม่เหล็ก"}
                    onClick={() =>
                      setShowExtraActions((v) => {
                        const next = !v;
                        setShowCloverActions(false);
                        setShowFlowerActions(false);
                        return next;
                      })
                    }
                  >
                    <span className="fs-3 mb-1" aria-hidden="true">🍜</span>
                  </button>

                  <button
                    type="button"
                    className={`btn rounded-circle d-inline-flex align-items-center justify-content-center ${
                      showCloverActions ? "btn-success" : "btn-outline-success"
                    }`}
                    style={{ width: 60, height: 60 }}
                    aria-label={showCloverActions ? "ซ่อนปุ่มใบ clover" : "แสดงปุ่มใบ clover"}
                    title={showCloverActions ? "ซ่อนปุ่มใบ clover" : "แสดงปุ่มใบ clover"}
                    onClick={() =>
                      setShowCloverActions((v) => {
                        const next = !v;
                        setShowExtraActions(false);
                        setShowFlowerActions(false);
                        return next;
                      })
                    }
                  >
                    <span className="fs-3 mb-1" aria-hidden="true">🍀</span>
                  </button>

                  <button
                    type="button"
                    className={`btn rounded-circle d-inline-flex align-items-center justify-content-center ${
                      showFlowerActions ? "btn-warning" : "btn-outline-warning"
                    }`}
                    style={{ width: 60, height: 60 }}
                    aria-label={showFlowerActions ? "ซ่อนปุ่มกระถางและดอกไม้" : "แสดงปุ่มกระถางและดอกไม้"}
                    title={showFlowerActions ? "ซ่อนปุ่มกระถางและดอกไม้" : "แสดงปุ่มกระถางและดอกไม้"}
                    onClick={() =>
                      setShowFlowerActions((v) => {
                        const next = !v;
                        setShowExtraActions(false);
                        setShowCloverActions(false);
                        setShowKeychainPanel(false);
                        return next;
                      })
                    }
                  >
                    <span className="fs-3 mb-1" aria-hidden="true">🌼</span>
                  </button>

                  <button
                    type="button"
                    className={`btn rounded-circle d-inline-flex align-items-center justify-content-center ${
                      showKeychainPanel ? "btn-info" : "btn-outline-info"
                    }`}
                    style={{ width: 60, height: 60 }}
                    aria-label={showKeychainPanel ? "ซ่อนพวงกุญแจ" : "แสดงพวงกุญแจ"}
                    title={showKeychainPanel ? "ซ่อนพวงกุญแจ" : "แสดงพวงกุญแจ"}
                    disabled={items.length > 0}
                    onClick={async () => {
                      const next = !showKeychainPanel;
                      setShowKeychainPanel(next);
                      setShowExtraActions(false);
                      setShowCloverActions(false);
                      setShowFlowerActions(false);
                      if (next && keychainOptions.length === 0) {
                        setKeychainOptionsLoading(true);
                        try {
                          const r = await fetch(apiUrl("/keychain_options_list"));
                          const d = await r.json().catch(() => ({}));
                          const opts: KeychainOption[] = Array.isArray(d.items) ? d.items : [];
                          setKeychainOptions(opts);
                          if (opts.length > 0) { setKeychainSelectedId(opts[0].id); }
                        } catch { /* silent */ } finally {
                          setKeychainOptionsLoading(false);
                        }
                      }
                    }}
                  >
                    <span className="fs-3 mb-1" aria-hidden="true">🗝️</span>
                  </button>
                </div>
                
                {/* เมื่อ showExtraActions เป็น true => โชว์ปุ่มไอคอน 2 ปุ่ม */}
                {showExtraActions && (
                <div className="mb-3">
                  <div className="card border-0 bg-light rounded-3 shadow-sm">
                    <div className="card-body py-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="small text-muted">เพิ่มแม่เหล็กแบบลัด</span>
                        <span className="badge bg-secondary-subtle text-secondary-emphasis">
                          Quick add
                        </span>
                      </div>

                      <div className="row g-2 mb-2">

                        <div className="col-6">
                          <button
                            type="button"
                            className="btn btn-outline-dark w-100 h-100 rounded-3 d-flex flex-column justify-content-center align-items-center py-2"
                            onClick={() => {
                              setShowManual(true);
                              if (manualInputRef.current) manualInputRef.current.value = "PD351";
                              addProductByCode("PD351");
                            }}
                          >
                            <span className="fs-3 mb-1">🧲</span>
                            <span className="fw-semibold small">แม่เหล็กเล็ก</span>
                            <span className="small text-muted">รหัส PD351</span>
                          </button>
                        </div>

                        <div className="col-6">
                          <button
                            type="button"
                            className="btn btn-outline-dark w-100 h-100 rounded-3 d-flex flex-column justify-content-center align-items-center py-2"
                            onClick={() => {
                              setShowManual(true);
                              if (manualInputRef.current) manualInputRef.current.value = "PD350";
                              addProductByCode("PD350");
                            }}
                          >
                            <span className="fs-3 mb-1">🧲</span>
                            <span className="fw-semibold small">แม่เหล็กใหญ่</span>
                            <span className="small text-muted">รหัส PD350</span>
                          </button>
                        </div>

                        
                      </div>

                      {/* 🔁 ปุ่มเปิด/ปิดดีลแม่เหล็ก */}
                      <div className="mt-2">
                        <button
                          type="button"
                          className={
                            "btn btn-sm w-100 " +
                            (enableMagnetPromo ? "btn-outline-danger" : "btn-outline-success")
                          }
                          onClick={() => setEnableMagnetPromo((v) => !v)}
                        >
                          {enableMagnetPromo ? "ปิดดีลราคาพิเศษแม่เหล็ก" : "เปิดดีลราคาพิเศษแม่เหล็ก"}
                        </button>
                        <div className="small text-muted text-center mt-1">
                          สถานะดีล:{" "}
                          <span className={enableMagnetPromo ? "text-danger" : "text-success"}>
                            {enableMagnetPromo ? "เปิดใช้งาน" : "ปิดอยู่ (คิดราคาเต็ม)"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                )}

                {showCloverActions && (
                  <div className="mb-3 mt-2">
                    <div className="card border-0 bg-light rounded-3 shadow-sm">
                      <div className="card-body py-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="small text-muted">เพิ่มใบ clover แบบลัด</span>
                          <span className="badge bg-success-subtle text-success-emphasis">Quick add</span>
                        </div>

                        <div className="row g-2">
                        {CLOVER_CODES.map((code, idx) => {
                        const name = CLOVER_NAME[idx];
                        const btnClass = cloverBtnClass(name);

                        return (
                          <div className="col-6" key={code}>
                            <button
                              type="button"
                              className={`btn ${btnClass} w-100 h-100 rounded-3 d-flex flex-column justify-content-center align-items-center py-2`}
                              onClick={() => {
                                setShowManual(true);
                                if (manualInputRef.current) manualInputRef.current.value = code;
                                addProductByCode(code);
                              }}
                            >
                              <span className="fs-3 mb-1">🍀</span>

                              <span className="fw-semibold small">
                                ใบ Clover – {name}
                              </span>

                              <span className="small text-muted">
                                รหัส {code.toUpperCase()}
                              </span>
                            </button>
                          </div>
                        );
                      })}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {showFlowerActions && (
                  <div className="mb-3 mt-2">
                    <div className="card border-0 bg-light rounded-3 shadow-sm">
                      <div className="card-body py-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="small text-muted">เพิ่มกระถางและดอกไม้จากรายการจริง</span>
                          <span className="badge bg-warning-subtle text-warning-emphasis">Live items</span>
                        </div>

                        {scanSpecialError && (
                          <div className="alert alert-warning py-2 px-3 mb-3">{scanSpecialError}</div>
                        )}

                        {scanSpecialLoading ? (
                          <div className="small text-muted">กำลังโหลดรายการกระถางและดอกไม้…</div>
                        ) : (
                          <div className="d-flex flex-column gap-3">
                            <div>
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="fw-semibold small text-uppercase text-muted">กระถาง</span>
                                <span className="small text-muted">{potSpecialItems.length} รายการ</span>
                              </div>
                              {potSpecialItems.length === 0 ? (
                                <div className="small text-muted">ยังไม่มีสินค้ากระถางในปุ่มลัด</div>
                              ) : (
                                <div className="d-flex gap-2 overflow-auto pb-1">
                                  {potSpecialItems.map((item) => (
                                    <button
                                      key={item.id}
                                      type="button"
                                      className="btn btn-outline-warning rounded-3 d-flex flex-column align-items-start text-start flex-shrink-0"
                                      style={{ minWidth: 180 }}
                                      onClick={() => {
                                        setShowManual(true);
                                        if (manualInputRef.current) manualInputRef.current.value = item.product_code;
                                        void addProductByCode(item.product_code);
                                      }}
                                    >
                                      <span className="fw-semibold">{item.product.name}</span>
                                      <span className="small text-muted">{item.product.code}</span>
                                      <span className="small text-dark">฿{item.product.price.toFixed(2)}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>

                            <div>
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="fw-semibold small text-uppercase text-muted">ดอกไม้</span>
                                <span className="small text-muted">{flowerSpecialItems.length} รายการ</span>
                              </div>
                              {flowerSpecialItems.length === 0 ? (
                                <div className="small text-muted">ยังไม่มีสินค้าดอกไม้ในปุ่มลัด</div>
                              ) : (
                                <div className="d-flex gap-2 overflow-auto pb-1">
                                  {flowerSpecialItems.map((item) => (
                                    <button
                                      key={item.id}
                                      type="button"
                                      className="btn btn-outline-danger rounded-3 d-flex flex-column align-items-start text-start flex-shrink-0"
                                      style={{ minWidth: 180 }}
                                      onClick={() => {
                                        setShowManual(true);
                                        if (manualInputRef.current) manualInputRef.current.value = item.product_code;
                                        void addProductByCode(item.product_code);
                                      }}
                                    >
                                      <span className="fw-semibold">{item.product.name}</span>
                                      <span className="small text-muted">{item.product.code}</span>
                                      <span className="small text-dark">฿{item.product.price.toFixed(2)}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {showKeychainPanel && (
                  <div className="mb-3 mt-2">
                    <div className="card border-0 bg-light rounded-3 shadow-sm">
                      <div className="card-body py-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <span className="small text-muted fw-semibold">เพิ่มพวงกุญแจ/เครื่องประดับ</span>
                          <span className="badge bg-info-subtle text-info-emphasis">keychain stock</span>
                        </div>

                        {keychainOptionsLoading ? (
                          <div className="small text-muted">กำลังโหลดรายการ…</div>
                        ) : keychainOptions.length === 0 ? (
                          <div className="small text-muted">ไม่พบรายการพวงกุญแจ</div>
                        ) : (
                          <>
                            <div className="mb-2">
                              <label className="form-label small mb-1">เลือกรายการ</label>
                              <select
                                className="form-select form-select-sm"
                                value={keychainSelectedId}
                                onChange={(e) => setKeychainSelectedId(Number(e.target.value))}
                              >
                                {keychainOptions.map((opt) => (
                                  <option key={opt.id} value={opt.id}>
                                    {opt.label} (stock: {opt.stock_on_hand})
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="row g-2 mb-2">
                              <div className="col-4">
                                <label className="form-label small mb-1">จำนวน</label>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  min={0}
                                  value={keychainQtyInput}
                                  onChange={(e) => {
                                    const v = parseInt(e.target.value);
                                    setKeychainQtyInput(isNaN(v) ? 0 : Math.max(0, v));
                                  }}
                                />
                              </div>
                              <div className="col-4">
                                <label className="form-label small mb-1">ราคาขาย (บาท)</label>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  min={0}
                                  value={keychainPriceInput}
                                  onChange={(e) => setKeychainPriceInput(Math.max(0, parseFloat(e.target.value) || 0))}
                                />
                              </div>
                              <div className="col-4 d-flex align-items-end">
                                <button
                                  type="button"
                                  className="btn btn-info btn-sm w-100"
                                  disabled={keychainSelectedId === 0 || keychainQtyInput <= 0 || keychainPriceInput < 0}
                                  onClick={() => {
                                    const opt = keychainOptions.find((o) => o.id === keychainSelectedId);
                                    if (!opt) return;
                                    setKeychainItems((prev) => {
                                      const idx = prev.findIndex((it) => it.keychainId === opt.id);
                                      if (idx >= 0) {
                                        const updated = [...prev];
                                        updated[idx] = { ...updated[idx], qty: updated[idx].qty + keychainQtyInput, unitPrice: keychainPriceInput };
                                        return updated;
                                      }
                                      return [...prev, { keychainId: opt.id, label: opt.label, qty: keychainQtyInput, unitPrice: keychainPriceInput }];
                                    });
                                  }}
                                >
                                  + เพิ่ม
                                </button>
                              </div>
                            </div>
                          </>
                        )}

                        {keychainItems.length > 0 && (
                          <div className="mt-3">
                            <div className="small text-muted mb-1 fw-semibold">รายการที่เพิ่มแล้ว</div>
                            {keychainItems.map((it, idx) => (
                              <div key={it.keychainId} className="d-flex align-items-center gap-2 mb-1 small">
                                <span className="flex-fill">{it.label}</span>
                                <span className="text-muted">×{it.qty}</span>
                                <span className="fw-semibold">฿{it.unitPrice.toFixed(0)}</span>
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm py-0 px-1"
                                  onClick={() => setKeychainItems((prev) => prev.filter((_, i) => i !== idx))}
                                >✕</button>
                              </div>
                            ))}
                            <div className="small text-muted mt-2">
                              รวม {keychainTotalQty} ชิ้น · ฿{keychainGrandTotal.toFixed(0)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* รายการสินค้า */}
                {items.length > 0 && (
                <div className="mb-3">
                  {items.map((entry, idx) => {
                    const unitPrice = getUnitPrice(entry.product, entry.qty);

                    return (
                      <div
                        key={`${entry.product.code}-${idx}`}
                        className="card border rounded-3 shadow-sm mb-2"
                        style={{ borderColor: "#007bff", borderWidth: "2px" }}
                      >
                         {/* ปุ่ม X ลบรายการ */}
                        <div className="position-relative pb-3">
                          <button 
                            type="button"
                            className="btn-close position-absolute"
                            style={{
                              top: "6px",
                              right: "6px",
                              filter: "brightness(0) invert(0.3)" // ให้ดูเทาๆ สวยกว่า
                            }}
                            aria-label="Close"
                            onClick={() => removeItem(idx)}
                          ></button>
                        </div>
                        <div className="card-body py-3">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div>
                              <div className="small text-secondary">รหัสสินค้า</div>
                              <div className="fw-semibold text-primary">{entry.product.code}</div>
                            </div>
                            <div className="text-end">
                              <div className="small text-secondary">ราคา</div>
                              <div className="fs-5 fw-bold text-primary">
                                ฿{unitPrice.toFixed(2)}
                              </div>
                            </div>
                          </div>

                          <div className="fw-semibold text-dark">{entry.product.name}</div>

                          <div className="row g-2 align-items-center mt-2">
                            <div className="col-auto small text-secondary">จำนวน</div>
                            <div className="col-4 col-sm-3 col-md-3">
                              <input
                                type="number"
                                min={1}
                                step={1}
                                className="form-control"
                                value={entry.qty}
                                onChange={(e) => setQty(idx, Number(e.target.value))}
                              />
                            </div>
                            {entry.product.unit && (
                              <div className="col-auto small text-muted">
                                หน่วย: {entry.product.unit}
                              </div>
                            )}
                          </div>

                          <div className=" bg-white shadow-sm mt-1 px-3 pb-2">
                            <div className="d-flex align-items-center justify-content-end gap-2">
                              <div className="small text-black">ราคารวม</div>
                              <div className="fs-5 fw-bold">
                                {(unitPrice * entry.qty).toLocaleString(undefined, {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                                })}
                              </div>
                            </div>

                          </div>
                          {/* <button
                            type="button"
                            className="btn btn-outline-danger w-100 mt-2"
                            onClick={() => removeItem(idx)}
                          >
                            ลบรายการนี้
                          </button> */}
                        </div>
                      </div>
                    );
                  })}
                </div>
                )}


                {errorMsg && (
                  <div className="alert alert-warning rounded-3" role="alert">
                    <div className="fw-semibold">ข้อผิดพลาด:</div>
                    <div className="text-break">{errorMsg}</div>
                  </div>
                )}

                <div className="d-grid gap-2 mb-3">

                  {/* ✅ ทางเลือกใหม่: ถ่าย/อัปโหลดรูปแล้วถอดรหัส */}
                  {/* <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    📷 ถ่าย/อัปโหลดรูป QR
                  </button> */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"   // มือถือจะเปิดกล้องหลังเป็นตัวเลือก
                    className="d-none"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) decodeFromImageFile(f);
                      e.currentTarget.value = ""; // รีเซ็ตให้เลือกไฟล์ซ้ำได้
                    }}
                  />

                </div>

                <div className="d-none scan-sale-channel-topbar rounded-4 p-3 mb-3">
                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2">
                    <div className="d-flex flex-column">
                      <span className="small fw-semibold scan-sale-channel-kicker">สถานที่ขาย</span>
                      <span className="small text-muted">เลือกให้ถูกก่อนบันทึกบิล</span>
                    </div>

                    <div className="scan-sale-channel-select-wrap">
                      <select
                        className="form-select form-select-sm scan-sale-channel-select"
                        value={saleChannel}
                        onChange={(e) => setSaleChannel(e.target.value)}
                      >
                        {visibleSaleChannels.map((channel) => (
                          <option key={channel.id} value={channel.code}>
                            {channel.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div
                    className={`scan-sale-channel-summary mt-2 ${
                      saleChannelTheme === "main"
                        ? "scan-sale-channel-summary-main"
                        : saleChannelTheme === "booth"
                          ? "scan-sale-channel-summary-booth"
                          : "scan-sale-channel-summary-custom"
                    }`}
                  >
                    กำลังบันทึกการขายเป็น: <strong>{saleChannelLabel}</strong>
                  </div>
                </div>

                {/* การ์ดยอดรวมทั้งบิล */}
                <div
                  className={`card text-light shadow-sm mt-4 ${
                    saleChannelTheme === "main"
                      ? "scan-total-card scan-total-card-main"
                      : saleChannelTheme === "booth"
                        ? "scan-total-card scan-total-card-booth"
                        : "scan-total-card scan-total-card-custom"
                  }`}
                  style={{ borderWidth: "8px" }}
                >
                  <div className="card-body">
                    <div className="fw-semibold small mb-2">💰 ยอดรวมบิลทั้งหมด</div>

                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="badge bg-light text-primary">
                        จำนวนชิ้นรวม: <span className="fw-bold">{isKeychainBill ? keychainTotalQty : totalQty}</span>
                      </span>
                      <div className="text-end">
                        <div className="small">ยอดก่อนหักส่วนลด</div>
                        <div className="fs-5 fw-bold">{(isKeychainBill ? keychainGrandTotal : grandTotal).toLocaleString(undefined, { maximumFractionDigits: 0 })} บาท</div>
                      </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center">
                    
                      <div className="small">ส่วนลด</div>
                      <div className="d-flex align-items-center gap-2">
                        <div className="fs-5">- {discount.toLocaleString(undefined, { maximumFractionDigits: 0 })} บาท</div>
                        {/* ปุ่มเปิด/ปิดแถบส่วนลด (สไตล์เหมือน “พิมพ์รหัสด้วยตนเอง”) */}
                        <button
                          type="button"
                          className="btn btn-outline-light btn-sm"
                          aria-expanded={showDiscountPanel}
                          onClick={() => {
                            setDraftDiscount(discount); // sync ค่าเดิมเข้า draft
                            setDiscountErr("");
                            setShowDiscountPanel((s) => !s);
                          }}
                        >
                          {showDiscountPanel ? "ซ่อนส่วนลด" : "ตั้งค่าส่วนลด (ตัวเลือก)"}
                        </button>
                      </div>
                    </div>

                    {/* แผงส่วนลดแบบพับลง */}
                    {showDiscountPanel && (
                      <div className="border rounded-3 p-3 mt-2 bg-white">
                        <div className="mb-2 small text-muted">
                          ยอดก่อนหักส่วนลด:{" "}
                          <b>{effectiveGrandTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })} บาท</b>
                        </div>

                        <label className="form-label">จำนวนเงินส่วนลด (บาท)</label>
                        <div className="input-group mb-2">
                          <span className="input-group-text">฿</span>
                          <input
                            type="number"
                            className="form-control"
                            min={0}
                            step={1}
                            value={Number.isFinite(draftDiscount) ? draftDiscount : 0}
                            onChange={(e) => {
                              const v = Math.floor(Number(e.target.value) || 0);
                              setDraftDiscount(v);
                              if (v < 0) setDiscountErr("ส่วนลดต้องเป็นจำนวนเต็มไม่ติดลบ");
                              else if (v > effectiveGrandTotal) setDiscountErr("ส่วนลดมากกว่ายอดก่อนหักส่วนลดไม่ได้");
                              else setDiscountErr("");
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                const v = Math.max(0, Math.min(effectiveGrandTotal, Math.floor(draftDiscount || 0)));
                                if (!discountErr) setDiscount(v);
                              }
                            }}
                          />

                          {/* ปุ่มเพิ่มส่วนลด */}
                          {[ 20, 50, 100].map((amt) => (
                            <button
                              key={`plus${amt}`}
                              className="btn btn-outline-secondary"
                              type="button"
                              onClick={() => {
                                const v = Math.min(effectiveGrandTotal, Math.max(0, draftDiscount + amt));
                                setDraftDiscount(v);
                                setDiscountErr(v > effectiveGrandTotal ? "ส่วนลดมากกว่ายอดก่อนหักส่วนลดไม่ได้" : "");
                              }}
                              title={`-${amt} บาท`}
                            >
                              -{amt}
                            </button>
                          ))}



                          {/* ปุ่มเคลียร์ */}
                          <button
                            className="btn btn-outline-secondary"
                            type="button"
                            onClick={() => {
                              setDraftDiscount(0);
                              setDiscountErr("");
                            }}
                          >
                            เคลียร์
                          </button>
                        </div>


                        {/* ปุ่มเปอร์เซ็นต์ช่วยคิดเร็ว */}
                        <div className="d-flex flex-wrap gap-2 mb-2">
                          {[5, 10, 15, 20].map((pct) => (
                            <button
                              key={pct}
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => {
                                const v = Math.floor((effectiveGrandTotal * pct) / 100);
                                setDraftDiscount(v);
                                setDiscountErr(v > effectiveGrandTotal ? "ส่วนลดมากกว่ายอดก่อนหักส่วนลดไม่ได้" : "");
                              }}
                            >
                              {pct}% ≈ {Math.floor((effectiveGrandTotal * pct) / 100).toLocaleString()}฿
                            </button>
                          ))}
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => {
                              const v = effectiveGrandTotal;
                              setDraftDiscount(v);
                              setDiscountErr("");
                            }}
                          >
                            เททั้งยอด
                          </button>
                        </div>

                        {discountErr && (
                          <div className="alert alert-warning py-2 small">{discountErr}</div>
                        )}

                        <div className="border rounded-3 p-3 bg-light">
                          <div className="d-flex justify-content-between">
                            <div>สุทธิหลังหักส่วนลด</div>
                            <div className="fw-bold">
                              {Math.max(0, effectiveGrandTotal - (draftDiscount || 0)).toLocaleString(undefined, {
                                maximumFractionDigits: 0,
                              })}{" "}
                              บาท
                            </div>
                          </div>
                        </div>

                        <div className="d-flex gap-2 mt-3">
                          <button
                            type="button"
                            className="btn btn-primary flex-fill"
                            disabled={!!discountErr || draftDiscount < 0 || draftDiscount > effectiveGrandTotal}
                            onClick={() => {
                              const v = Math.max(0, Math.min(effectiveGrandTotal, Math.floor(draftDiscount || 0)));
                              setDiscount(v);
                              setShowDiscountPanel(false); // ยุบแผงหลังยืนยัน
                            }}
                          >
                            ใช้ส่วนลดนี้
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary flex-fill"
                            onClick={() => setShowDiscountPanel(false)}
                          >
                            ปิดแถบ
                          </button>
                        </div>
                      </div>
                    )}
                   
                 
                    <hr className="border-light" />
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="fw-semibold">ยอดสุทธิ</div>
                      <div className="fs-3 fw-bold">{effectiveNetTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })} บาท</div>
                    </div>
                  </div>
                </div>

                {/* กล่องแสดงวิธีชำระเงินที่เลือก */}
                <div className="alert alert-secondary d-flex justify-content-between align-items-center rounded-3">
                  <div>
                    วิธีชำระเงิน: <strong>{paymentMethod ?? "ยังไม่เลือก"}</strong>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => setShowPaymentModal(true)}
                  >
                    เปลี่ยน
                  </button>
                </div>

                <div className="scan-sale-channel-topbar rounded-4 p-3 mb-3">
                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2">
                    <div className="d-flex flex-column">
                      <span className="small fw-semibold scan-sale-channel-kicker">สถานที่ขาย</span>
                      <span className="small text-muted">เลือกให้ถูกก่อนบันทึกบิล</span>
                    </div>

                    <div className="scan-sale-channel-select-wrap">
                      <select
                        className="form-select form-select-sm scan-sale-channel-select"
                        value={saleChannel}
                        onChange={(e) => setSaleChannel(e.target.value)}
                      >
                        {visibleSaleChannels.map((channel) => (
                          <option key={channel.id} value={channel.code}>
                            {channel.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div
                    className={`scan-sale-channel-summary mt-2 ${
                      saleChannelTheme === "main"
                        ? "scan-sale-channel-summary-main"
                        : saleChannelTheme === "booth"
                          ? "scan-sale-channel-summary-booth"
                          : "scan-sale-channel-summary-custom"
                    }`}
                  >
                    กำลังบันทึกการขายเป็น: <strong>{saleChannelLabel}</strong>
                  </div>
                  {visibleSaleChannels.length === 0 && (
                    <div className="alert alert-warning rounded-3 py-2 px-3 mt-2 mb-0">
                      บัญชีนี้ยังไม่ได้รับสิทธิ์ให้ขายในสาขาใด โปรดติดต่อผู้ดูแลระบบ
                    </div>
                  )}
                </div>

                {isTikTokSale && (
                  <div className="border rounded-4 bg-white shadow-sm p-3 mb-3">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mb-3">
                      <div className="d-flex flex-column">
                        <span className="small fw-semibold text-primary">Package Fee</span>
                        <span className="small text-muted">
                          เลือกแพ็กเกจจาก stock จริงแบบเดียวกับหน้า TikTok
                        </span>
                      </div>
                      {selectedTikTokPackage && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm align-self-start align-self-md-center"
                          onClick={clearTikTokPackage}
                        >
                          ล้างแพ็กเกจ
                        </button>
                      )}
                    </div>

                    {/* PMN-CRITICAL(/scan): saved to orders.tiktok_order_no — same column shown/edited on TikTokShopOrdersPage */}
                    <div className="mb-3">
                      <label htmlFor="scan-tiktok-order-no" className="form-label small text-muted mb-1">
                        TikTok Order No <span className="text-danger">*</span>
                      </label>
                      <input
                        id="scan-tiktok-order-no"
                        ref={tiktokOrderNoInputRef}
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        className={`form-control ${!tiktokOrderNo ? "border-danger" : ""}`}
                        placeholder="เช่น 578912345678901234"
                        value={tiktokOrderNo}
                        maxLength={30}
                        onChange={(e) => setTiktokOrderNo(e.target.value.replace(/\D/g, "").slice(0, 30))}
                      />
                      <div className="d-flex flex-wrap justify-content-between gap-2 mt-1 small">
                        <span className="text-muted">เลขนี้จะแสดงในหน้า TikTok Shop Orders</span>
                        {tiktokOrderNo ? (
                          <span className={tiktokOrderNo.length === 18 ? "text-success" : "text-warning"}>
                            {tiktokOrderNo.length} หลัก
                            {tiktokOrderNo.length !== 18 ? " (ปกติ 18 หลัก — ตรวจอีกครั้ง)" : ""}
                          </span>
                        ) : (
                          <span className="text-danger">ต้องกรอกก่อนบันทึก</span>
                        )}
                      </div>
                    </div>

                    <div className="row g-3 align-items-end">
                      <div className="col-12 col-md-4">
                        <label className="form-label small text-muted mb-1">Package Fee</label>
                        <input
                          type="text"
                          className="form-control bg-light text-dark"
                          value={selectedTikTokPackageFee.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                          readOnly
                        />
                      </div>
                      <div className="col-12 col-md-8">
                        <label className="form-label small text-muted mb-1">แพ็กเกจที่เลือก</label>
                        <div className="form-control bg-light text-dark d-flex align-items-center">
                          {selectedTikTokPackage
                            ? `${selectedTikTokPackage.product.name} (${selectedTikTokPackage.product_code})`
                            : "ยังไม่ได้เลือกแพ็กเกจ"}
                        </div>
                      </div>
                    </div>

                    <div className="small text-muted mt-3 mb-2">
                      เลือกแพ็กเกจแล้วระบบจะเก็บต้นทุนและรหัสแพ็กเกจไปพร้อมบิล TikTok
                    </div>

                    {scanSpecialLoading ? (
                      <div className="small text-muted">กำลังโหลดรายการแพ็กเกจ...</div>
                    ) : scanSpecialError ? (
                      <div className="alert alert-warning py-2 px-3 mb-0 small">{scanSpecialError}</div>
                    ) : packageSpecialItems.length === 0 ? (
                      <div className="alert alert-secondary py-2 px-3 mb-0 small">
                        ยังไม่มีสินค้าในหมวดแพ็กเกจ ให้เพิ่มจากหน้าจัดการ Scan Special Items ก่อน
                      </div>
                    ) : (
                      <div className="d-flex gap-2 overflow-auto pb-1">
                        {packageSpecialItems.map((item) => {
                          const availableQty = Math.max(0, Number(item.product.available_qty ?? 0));
                          const estimatedCost = item.product.estimated_unit_cost;
                          const isDisabled = availableQty <= 0 || estimatedCost == null;
                          const isSelected = item.product_code === selectedTikTokPackageCode;

                          return (
                            <button
                              key={item.id}
                              type="button"
                              className={`btn text-start border rounded-4 px-3 py-2 flex-shrink-0 ${
                                isSelected ? "btn-primary" : "btn-outline-primary"
                              }`}
                              style={{ minWidth: "220px" }}
                              onClick={() => chooseTikTokPackage(item)}
                              disabled={isDisabled}
                            >
                              <div className="d-flex align-items-baseline gap-2">
                                <span className="fw-semibold">{item.product.name}</span>
                                <span
                                  className={`small ${isSelected ? "text-white-50" : "text-muted"}`}
                                >
                                  เหลือ {availableQty}
                                </span>
                              </div>
                              <div className={`small ${isSelected ? "text-white-50" : "text-muted"}`}>
                                {item.product_code}
                              </div>
                              <div className="fw-semibold">
                                {estimatedCost == null
                                  ? "สต็อกไม่พอ"
                                  : `฿${estimatedCost.toLocaleString(undefined, {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}`}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* ปุ่มบันทึกข้อมูลการขาย */}
                <div className="d-none scan-sale-channel-card rounded-4 p-3 p-md-4 mb-3">
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                    <div>
                      <div className="text-uppercase small fw-semibold scan-sale-channel-kicker">
                        สถานที่ขาย
                      </div>
                      <div className="fw-bold fs-5 text-dark">เลือกก่อนบันทึกบิล</div>
                      <div className="text-muted small">
                        ใช้เพื่อแยกบิลที่ขายหน้าร้านกับบิลที่ขายเวลาออกบูท
                      </div>
                    </div>

                    <div className="scan-sale-channel-select-wrap">
                      <select
                        className="form-select scan-sale-channel-select"
                        value={saleChannel}
                        onChange={(e) => setSaleChannel(e.target.value)}
                      >
                        {visibleSaleChannels.map((channel) => (
                          <option key={channel.id} value={channel.code}>
                            {channel.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div
                    className={`scan-sale-channel-summary mt-3 ${
                      saleChannelTheme === "main"
                        ? "scan-sale-channel-summary-main"
                        : saleChannelTheme === "booth"
                          ? "scan-sale-channel-summary-booth"
                          : "scan-sale-channel-summary-custom"
                    }`}
                  >
                    กำลังบันทึกการขายเป็น: <strong>{saleChannelLabel}</strong>
                  </div>
                </div>

                <div className="d-grid mt-3">
                  <button
                    type="button"
                    className="btn btn-primary btn-lg"
                    disabled={
                      visibleSaleChannels.length === 0 ||
                      (items.length === 0 && keychainItems.length === 0) ||
                      saving ||
                      items.some((it) => it.qty <= 0)
                    }
                    onClick={async () => {
                      if (visibleSaleChannels.length === 0) {
                        setToast({ show: true, message: "บัญชีนี้ยังไม่มีสิทธิ์สาขาสำหรับบันทึกการขาย" });
                        return;
                      }
                      if (!paymentMethod) {
                        setToast({ show: true, message: "กรุณาเลือกวิธีชำระเงินก่อนบันทึก" });
                        setShowPaymentModal(true);
                        return;
                      }
                    
                      // กันเคสมีจำนวน = 0
                      if (items.some((it) => it.qty <= 0)) {
                        alert("จำนวนสินค้าทุกบรรทัดต้องมากกว่า 0");
                        return;
                      }

                      // PMN-CRITICAL(/scan): TikTok bills require TikTok Order No — checked before keychain path and stock pre-check/transfer popup.
                      if (isTikTokSale && !tiktokOrderNo) {
                        setToast({ show: true, message: "กรุณากรอก TikTok Order No" });
                        focusTikTokOrderNo();
                        return;
                      }

                      // keychain bill path
                      if (isKeychainBill) {
                        if (items.length > 0) {
                          alert("ไม่สามารถผสมสินค้าปกติกับพวงกุญแจในบิลเดียวกันได้");
                          return;
                        }
                        if (saving) return;
                        setSaving(true);
                        try {
                          const r = await fetch(apiUrl("/orders/prenext_bill_no"));
                          const j = await r.json().catch(() => ({}));
                          if (!r.ok) throw new Error(j?.error ?? r.statusText);
                          const last = j?.next;
                          const pad = (n: number, width = 3) => String(n).padStart(width, "0");
                          const billNo = last == null ? pad(0) : String(last);
                          const nextBillNo = String((parseInt(billNo, 10) || 0) + 1).padStart(billNo.length || 3, "0");

                          const now = new Date();
                          const toBEThaiDate = (d: Date) => {
                            const tz = "Asia/Bangkok";
                            const parts = new Intl.DateTimeFormat("en-GB", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(d);
                            const day = parts.find((p) => p.type === "day")?.value!;
                            const month = parts.find((p) => p.type === "month")?.value!;
                            const year = parts.find((p) => p.type === "year")?.value!;
                            return `${day}/${month}/${String(Number(year) + 543)}`;
                          };
                          const toThaiTime = (d: Date) => new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Bangkok", hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" }).format(d);

                          const kcPayload = {
                            paymentMethod,
                            saleType: "retail" as const,
                            saleChannel,
                            "เลขบิล": nextBillNo,
                            "วันที่บันทึก": toBEThaiDate(now),
                            "เวลาบันทึก": toThaiTime(now),
                            items: keychainItems.map((it) => ({ keychainId: it.keychainId, label: it.label, qty: it.qty, unitPrice: it.unitPrice })),
                            "ราคาทั้งบิล": keychainGrandTotal,
                            "จำนวนชิ้นรวม": keychainTotalQty,
                            tiktokOrderNo: isTikTokSale ? tiktokOrderNo : null,
                          };

                          const { resp, data, cancelled } = await postSaleWithDupConfirm("/sales_save_keychain", kcPayload);
                          if (cancelled) {
                            setToast({ show: true, message: "ยกเลิกการบันทึก — ตรวจ TikTok Order No อีกครั้ง" });
                            focusTikTokOrderNo();
                            setSaving(false);
                            return;
                          }
                          if (!resp.ok) {
                            if (data?.error === "keychain_stock_not_enough") {
                              alert("stock พวงกุญแจไม่เพียงพอ");
                            } else {
                              alert(`บันทึกบิลพวงกุญแจไม่สำเร็จ: ${describeSaleSaveError(data, resp.statusText)}`);
                            }
                            setSaving(false);
                            return;
                          }
                          alert(`บันทึกข้อมูลการขายพวงกุญแจเรียบร้อย! เลขบิล: ${nextBillNo}`);
                          resetUIAfterSave(nextBillNo);
                        } catch (e: any) {
                          console.error("❌ Keychain save error:", e);
                          alert(e?.message ?? "เกิดข้อผิดพลาดระหว่างบันทึก");
                        } finally {
                          setSaving(false);
                        }
                        return;
                      }

                      if (saving) return;

                      // PMN-CRITICAL(/scan): TikTok/MAIN_SHOP sales must check location stock before /sales_save; shortages open the transfer flow.
                      // Pre-check TIKTOK stock ก่อน save
                      if (isTikTokSale) {
                        const checkResp = await fetch(apiUrl("/stock/check-tiktok"), {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({
                            items: items.map((it) => ({
                              product_code: it.product.code,
                              qty: it.qty,
                              product_name: it.product.name,
                            })),
                          }),
                        });
                        const checkData = await checkResp.json().catch(() => ({}));
                        if (checkResp.ok && Array.isArray(checkData.shortages) && checkData.shortages.length > 0) {
                          setTransferTarget("TIKTOK");
                          setTransferShortages(
                            checkData.shortages.map((s: Omit<StockShortageItem, "transferSource" | "transferStatus">) => ({
                              ...s,
                              transferSource: s.shop_available >= s.shortage ? "SHOP" : s.main_available >= s.shortage ? "MAIN" : "SHOP",
                              transferStatus: "idle" as const,
                            }))
                          );
                          setTransferSaveError("");
                          setShowTransferPopup(true);
                          return;
                        }
                      }
                      // Pre-check SHOP stock เฉพาะ MAIN_SHOP ก่อน save
                      if (isShopSale) {
                        const checkResp = await fetch(apiUrl("/stock/check-shop"), {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({
                            items: items.map((it) => ({
                              product_code: it.product.code,
                              qty: it.qty,
                              product_name: it.product.name,
                            })),
                          }),
                        });
                        const checkData = await checkResp.json().catch(() => ({}));
                        if (checkResp.ok && Array.isArray(checkData.shortages) && checkData.shortages.length > 0) {
                          setTransferTarget("SHOP");
                          setTransferShortages(
                            checkData.shortages.map((s: Omit<StockShortageItem, "transferSource" | "transferStatus">) => ({
                              ...s,
                              transferSource: s.tiktok_available >= s.shortage ? "TIKTOK" : s.main_available >= s.shortage ? "MAIN" : "TIKTOK",
                              transferStatus: "idle" as const,
                            }))
                          );
                          setTransferSaveError("");
                          setShowTransferPopup(true);
                          return;
                        }
                      }

                      setSaving(true);

                      try {
                        // PMN-CRITICAL(/scan): bill number and flattened payload shape are coupled to backend /sales_save.
                        let billNo = "000";
                        const r = await fetch(apiUrl("/orders/prenext_bill_no"));
                        const j = await r.json().catch(() => ({}));
                        if (!r.ok) throw new Error(j?.error ?? r.statusText);

                        const last = j?.next;
                        const pad = (n: number, width = 3) => String(n).padStart(width, "0");
                        billNo = last == null ? pad(0) : String(last);

                        const nextBillNo = String((parseInt(billNo, 10) || 0) + 1).padStart(billNo.length || 3, "0");

                        const now = new Date();
                        const toBEThaiDate = (d: Date) => {
                          const tz = "Asia/Bangkok";
                          const parts = new Intl.DateTimeFormat("en-GB", {
                            timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
                          }).formatToParts(d);
                          const day = parts.find((p) => p.type === "day")?.value!;
                          const month = parts.find((p) => p.type === "month")?.value!;
                          const year = parts.find((p) => p.type === "year")?.value!;
                          const beYear = String(Number(year) + 543);
                          return `${day}/${month}/${beYear}`;
                        };
                        const toThaiTime = (d: Date) =>
                          new Intl.DateTimeFormat("en-GB", {
                            timeZone: "Asia/Bangkok", hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit",
                          }).format(d);

                        const thaiDate = toBEThaiDate(now);
                        const thaiTime = toThaiTime(now);

                        const salesFlat = items.flatMap((it) => {
                          const unitPrice = getUnitPrice(it.product, it.qty);
                          return Array.from({ length: it.qty }, () => ({
                            "รหัสสินค้า": it.product.code,
                            "ชื่อสินค้า": it.product.name,
                            "จำนวน": 1,
                            "ราคา": unitPrice,
                          }));
                        });

                        const salesNumbered = salesFlat.map((row, i) => ({ "ลำดับ": i + 1, ...row }));

                        const payload = {
                          paymentMethod, // ตอนนี้มีค่าแน่นอนแล้ว
                          saleType: "retail" as const,
                          saleChannel,
                          saleChannelLabel,
                          packageFee: isTikTokSale ? selectedTikTokPackageFee : 0,
                          packageProductCode: isTikTokSale && selectedTikTokPackageCode
                            ? selectedTikTokPackageCode
                            : null,
                          tiktokOrderNo: isTikTokSale ? tiktokOrderNo : null,
                          "เลขบิล": nextBillNo,
                          "วันที่บันทึก": thaiDate,
                          "เวลาบันทึก": thaiTime,
                          ...Object.assign({}, ...salesNumbered.map((r, i) => ({ [i]: r }))),
                          "ราคาทั้งบิล": grandTotal,
                          "จำนวนชิ้นรวม": totalQty,
                          "ส่วนลด": discount,
                          "ยอดสุทธิ": netTotal,
                        };

                        const { resp: resp1, data: data1, cancelled } = await postSaleWithDupConfirm("/sales_save", payload);
                        if (cancelled) {
                          setToast({ show: true, message: "ยกเลิกการบันทึก — ตรวจ TikTok Order No อีกครั้ง" });
                          focusTikTokOrderNo();
                          setSaving(false);
                          return;
                        }
                        if (!resp1.ok) {
                          console.error("❌ Sales save failed:", data1);
                          alert(`บันทึกบิลไม่สำเร็จ: ${describeSaleSaveError(data1, resp1.statusText)}`);
                          setSaving(false);
                          return;
                        }

                        // PMN-NOTE(/scan): discount save is a post-save side effect; failure must not undo the already-saved bill.
                        // เซฟส่วนลด
                        try {
                          const resp2 = await fetch(apiUrl(`/sales/discounts/${encodeURIComponent(nextBillNo)}`), {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ discount_amount: discount, total_amount: netTotal }),
                          });
                          const data2 = await resp2.json().catch(() => ({}));
                          if (!resp2.ok) {
                            console.error("❌ Save discount failed:", data2);
                            alert(`บันทึกส่วนลดไม่สำเร็จ: ${data2?.error ?? resp2.statusText}`);
                          }
                        } catch (err) {
                          console.error("❌ API error (discount):", err);
                          alert("เชื่อมต่อ API ส่วนลดไม่ได้ — ตรวจสอบเซิร์ฟเวอร์/เน็ตเวิร์ค");
                        }

                        alert(`บันทึกข้อมูลการขายเรียบร้อย! เลขบิล: ${nextBillNo}`);
                        resetUIAfterSave(nextBillNo);
                      } catch (e: any) {
                        console.error("❌ Submit error:", e);
                        alert(e?.message ?? "เกิดข้อผิดพลาดระหว่างบันทึก");
                      } finally {
                        setSaving(false);
                      }
                    }}
                  >
                    {saving ? "กำลังบันทึก…" : "💾 บันทึกข้อมูลการขาย"}
                  </button>
                </div>

                {/* Draft buttons */}
                <div className="d-flex gap-2 mt-2">
                  <button
                    className="btn btn-outline-secondary flex-grow-1"
                    disabled={draftSaving || (items.length === 0 && keychainItems.length === 0)}
                    onClick={saveDraft}
                  >
                    {draftSaving ? "กำลังบันทึกดราฟ…" : "📋 บันทึกดราฟ"}
                  </button>
                  <button
                    className="btn btn-outline-secondary position-relative"
                    onClick={() => { void fetchDrafts(); setShowDraftPanel(true); }}
                  >
                    📂 ดราฟ
                    {drafts.length > 0 && (
                      <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                        {drafts.length}
                      </span>
                    )}
                  </button>
                </div>
                {loadedDraftId !== null && (
                  <div className="alert alert-warning py-1 mt-2 mb-0 small">
                    ⚠️ กำลังแก้ดราฟ #{loadedDraftId} — กด "บันทึก" เพื่อบันทึกและลบดราฟนี้อัตโนมัติ
                  </div>
                )}

                <div className="text-center mt-4">
                  <a href="/" className="btn btn-link text-decoration-none">
                    ← กลับหน้าเมนู
                  </a>
                </div>
              </div>
            </div>

            <div className="text-center small text-muted mt-3">
              * ต้องเปิดผ่าน <code>https</code> หรือ <code>http://localhost</code> เพื่อให้กล้องทำงาน โดยเฉพาะบนมือถือ
            </div>
          </div>
        </div>
      </div>

      {/* Transfer Popup — ย้าย stock เข้า location ปลายทางก่อนบันทึก */}
      {showTransferPopup && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: "rgba(0,0,0,0.55)", zIndex: 3000 }}
        >
          <div className="card shadow-lg border-0 rounded-4" style={{ maxWidth: 520, width: "94%" }}>
            <div className="card-body p-4">
              <h5 className="mb-1 fw-bold">⚠️ {transferTarget} stock ไม่เพียงพอ</h5>
              <p className="text-muted small mb-3">ย้าย stock เข้า {transferTarget} ก่อนบันทึก</p>

              <div className="d-flex flex-column gap-3 mb-3">
                {transferShortages.map((s, idx) => (
                  <div key={s.product_code} className="border rounded-3 p-3">
                    <div className="fw-semibold small">{s.product_code}</div>
                    <div className="small text-muted mb-2">{s.product_name}</div>
                    <div className="small mb-2">
                      มีใน {transferTarget} <strong>{getAvailableAtLocation(s, transferTarget)}</strong> / ต้องการ <strong>{s.needed}</strong>
                      {" → "}ขาด <strong className="text-danger">{s.shortage} ชิ้น</strong>
                    </div>

                    {s.transferStatus === "done" ? (
                      <div className="text-success small fw-semibold">✅ ย้ายแล้ว {s.shortage} ชิ้น</div>
                    ) : s.transferStatus === "error" ? (
                      <div className="text-danger small">❌ ย้ายไม่สำเร็จ ลองใหม่อีกครั้ง</div>
                    ) : (
                      <>
                        <div className="d-flex gap-3 mb-2">
                          {transferSourceOptions.map((loc) => {
                            const avail = getAvailableAtLocation(s, loc);
                            const disabled = avail < s.shortage;
                            return (
                              <label key={loc} className={`d-flex align-items-center gap-1 small ${disabled ? "text-muted" : ""}`}>
                                <input
                                  type="radio"
                                  name={`src-${s.product_code}`}
                                  value={loc}
                                  checked={s.transferSource === loc}
                                  disabled={disabled || s.transferStatus === "loading"}
                                  onChange={() =>
                                    setTransferShortages((prev) =>
                                      prev.map((x, i) => i === idx ? { ...x, transferSource: loc } : x)
                                    )
                                  }
                                />
                                {loc} (มี {avail}){disabled ? " — ไม่พอ" : ""}
                              </label>
                            );
                          })}
                        </div>
                        <button
                          type="button"
                          className="btn btn-warning btn-sm"
                          disabled={s.transferStatus === "loading" ||
                            getAvailableAtLocation(s, s.transferSource) < s.shortage}
                          onClick={async () => {
                            setTransferShortages((prev) =>
                              prev.map((x, i) => i === idx ? { ...x, transferStatus: "loading" } : x)
                            );
                            try {
                              // PMN-CRITICAL(/scan): this moves real stock before bill save; transferSaveError handles save failure afterward.
                              const resp = await fetch(apiUrl("/stock-transfers"), {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({
                                  product_code: s.product_code,
                                  from_location_code: s.transferSource,
                                  to_location_code: transferTarget,
                                  qty: s.shortage,
                                  note: "ย้ายอัตโนมัติก่อนบันทึกขาย",
                                }),
                              });
                              if (!resp.ok) throw new Error();
                              setTransferShortages((prev) =>
                                prev.map((x, i) => i === idx ? { ...x, transferStatus: "done" } : x)
                              );
                            } catch {
                              setTransferShortages((prev) =>
                                prev.map((x, i) => i === idx ? { ...x, transferStatus: "error" } : x)
                              );
                            }
                          }}
                        >
                          {s.transferStatus === "loading"
                            ? "กำลังย้าย…"
                            : `ย้าย ${s.shortage} ชิ้น จาก ${s.transferSource} → ${transferTarget}`}
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>

              {transferSaveError && (
                <div className="alert alert-warning small py-2 px-3 mb-3">
                  ⚠️ ย้าย stock สำเร็จแล้ว แต่บันทึกบิลไม่สำเร็จ<br />
                  stock ถูกย้ายไป {transferTarget} แล้ว กรุณาลองกด "บันทึก" อีกครั้ง<br />
                  หากยังไม่ได้ ติดต่อผู้ดูแลระบบ<br />
                  <span className="text-muted">(error: {transferSaveError})</span>
                </div>
              )}

              <div className="d-flex gap-2 justify-content-end">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  disabled={transferSaving}
                  onClick={() => {
                    setShowTransferPopup(false);
                    setTransferShortages([]);
                    setTransferSaveError("");
                  }}
                >
                  ยกเลิก
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={
                    transferSaving ||
                    !transferShortages.every((s) => s.transferStatus === "done")
                  }
                  onClick={async () => {
                    setTransferSaving(true);
                    setTransferSaveError("");
                    try {
                      // PMN-CRITICAL(/scan): transfer popup repeats the bill-save contract after all shortages are moved.
                      const r = await fetch(apiUrl("/orders/prenext_bill_no"));
                      const j = await r.json().catch(() => ({}));
                      if (!r.ok) throw new Error(j?.error ?? r.statusText);
                      const last = j?.next;
                      const pad = (n: number, w = 3) => String(n).padStart(w, "0");
                      const billNo = last == null ? pad(0) : String(last);
                      const nextBillNo = String((parseInt(billNo, 10) || 0) + 1).padStart(billNo.length || 3, "0");

                      const now = new Date();
                      const tz = "Asia/Bangkok";
                      const parts = new Intl.DateTimeFormat("en-GB", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(now);
                      const thaiDate = `${parts.find((p) => p.type === "day")?.value}/${parts.find((p) => p.type === "month")?.value}/${String(Number(parts.find((p) => p.type === "year")?.value) + 543)}`;
                      const thaiTime = new Intl.DateTimeFormat("en-GB", { timeZone: tz, hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" }).format(now);

                      const salesFlat = items.flatMap((it) => {
                        const unitPrice = getUnitPrice(it.product, it.qty);
                        return Array.from({ length: it.qty }, () => ({
                          "รหัสสินค้า": it.product.code,
                          "ชื่อสินค้า": it.product.name,
                          "จำนวน": 1,
                          "ราคา": unitPrice,
                        }));
                      });
                      const salesNumbered = salesFlat.map((row, i) => ({ "ลำดับ": i + 1, ...row }));
                      const payload = {
                        paymentMethod,
                        saleType: "retail" as const,
                        saleChannel,
                        saleChannelLabel,
                        packageFee: transferTarget === "TIKTOK" ? selectedTikTokPackageFee : 0,
                        packageProductCode: transferTarget === "TIKTOK" && selectedTikTokPackageCode
                          ? selectedTikTokPackageCode
                          : null,
                        tiktokOrderNo: isTikTokSale ? tiktokOrderNo : null,
                        "เลขบิล": nextBillNo,
                        "วันที่บันทึก": thaiDate,
                        "เวลาบันทึก": thaiTime,
                        ...Object.assign({}, ...salesNumbered.map((row, i) => ({ [i]: row }))),
                        "ราคาทั้งบิล": grandTotal,
                        "จำนวนชิ้นรวม": totalQty,
                        "ส่วนลด": discount,
                        "ยอดสุทธิ": netTotal,
                      };

                      const { resp: resp1, data: data1, cancelled } = await postSaleWithDupConfirm("/sales_save", payload);
                      if (cancelled) {
                        setTransferSaveError("ยกเลิกการบันทึก — แก้ TikTok Order No แล้วกดบันทึกอีกครั้ง");
                        return;
                      }
                      if (!resp1.ok) {
                        setTransferSaveError(describeSaleSaveError(data1, resp1.statusText));
                        return;
                      }

                      // PMN-NOTE(/scan): discount remains non-blocking in the transfer-save path as well.
                      try {
                        await fetch(apiUrl(`/sales/discounts/${encodeURIComponent(nextBillNo)}`), {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ discount_amount: discount, total_amount: netTotal }),
                        });
                      } catch {}

                      setShowTransferPopup(false);
                      setTransferShortages([]);
                      setTransferSaveError("");
                      alert(`บันทึกข้อมูลการขายเรียบร้อย! เลขบิล: ${nextBillNo}`);
                      resetUIAfterSave(nextBillNo);
                    } catch (e: any) {
                      setTransferSaveError(e?.message ?? "เกิดข้อผิดพลาด");
                    } finally {
                      setTransferSaving(false);
                    }
                  }}
                >
                  {transferSaving
                    ? "กำลังบันทึก…"
                    : transferShortages.every((s) => s.transferStatus === "done")
                      ? "✅ บันทึก"
                      : "บันทึก (ย้ายยังไม่ครบ)"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Draft Panel Modal */}
      {showDraftPanel && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: "rgba(0,0,0,0.55)", zIndex: 3000 }}
          onClick={() => setShowDraftPanel(false)}
        >
          <div
            className="bg-white rounded shadow"
            style={{ width: "min(480px, 95vw)", maxHeight: "80vh", display: "flex", flexDirection: "column" }}
            onClick={e => e.stopPropagation()}
          >
            <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
              <h6 className="mb-0 fw-bold">📂 ดราฟที่บันทึกไว้ ({drafts.length})</h6>
              <button className="btn-close" onClick={() => setShowDraftPanel(false)} />
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              {drafts.length === 0 && (
                <p className="text-center text-muted py-4">ยังไม่มีดราฟ</p>
              )}
              {drafts.map(d => (
                <div key={d.id} className="d-flex align-items-center border-bottom px-3 py-2 gap-2">
                  <div className="flex-grow-1">
                    <div className="fw-semibold small">{d.draft_note || `ดราฟ #${d.id}`}</div>
                    <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                      {new Date(d.created_at).toLocaleString("th-TH")}
                      {" · "}{d.item_count} ชิ้น
                      {" · "}฿{Number(d.grand_total).toLocaleString()}
                    </div>
                  </div>
                  <button className="btn btn-sm btn-primary" onClick={() => void loadDraft(d.id)}>โหลด</button>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => { if (window.confirm("ลบดราฟนี้?")) void deleteDraft(d.id); }}
                  >
                    ลบ
                  </button>
                </div>
              ))}
            </div>
            <div className="px-3 py-2 border-top d-flex justify-content-end">
              <button className="btn btn-secondary btn-sm" onClick={() => setShowDraftPanel(false)}>ปิด</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
